import { Inject, Injectable, Injector, LOCALE_ID } from '@angular/core';
import { Router } from '@angular/router';
import {
    BehaviorSubject,
    delay,
    distinctUntilChanged,
    filter,
    Observable,
    of,
    race,
    retry,
    Subject,
    switchMap,
    take,
    takeUntil,
    tap,
    timeout,
    timer,
} from 'rxjs';
import { RecordingService } from '../recording/recording.service';
import { ApiService } from '../api/api.service';
import DailyIframe, { DailyCall } from '@daily-co/daily-js';
import { ParticipantService } from '../participant/participant.service';
import { UserService } from '../user/user.service';
import { RoomService } from '../room/room.service';
import LogRocket from 'logrocket';
import { Conversation } from '@elevenlabs/client';
import { VideoTrackingService } from '../video-tracking/video-tracking.service';
import { AudioTrackingService } from '../audio-tracking/audio-tracking.service';
import { VisualAnalysisService } from '../visual-analysis/visual-analysis.service';
import { VisualObservation } from '../../models/visual-analysis.model';
import {
    QuantQuestion,
    QuantAnswer,
    QuantConfig,
    QuantFormat,
    QuantAnswerValue,
} from '../../models/quant-question.model';
import * as Sentry from '@sentry/angular';

interface Question {
    text: string;
}

interface ProbingRule {
    triggerType: string;
    triggerWords: string;
    probingLevel: number;
    followUpQuestion: string;
}

// Old format - for backward compatibility
interface LegacyProbingNote {
    rules: (ProbingRule | null)[];
}

// New format interfaces
interface GeneralProbingLevel {
    selectedLevel: number;
    explanations: { [key: string]: string };
}

interface SmartProbing {
    selectedType: 'smart_probing';
    rules: ProbingRule[];
}

interface GeneralProbing {
    selectedType: 'general_probing_level';
    generalProbingLevel: GeneralProbingLevel;
}

interface AiContext {
    selectedType: 'ai_context';
    aiContext: string;
}

// Union type supporting both old and new formats
type ProbingNote =
    | LegacyProbingNote
    | SmartProbing
    | GeneralProbing
    | AiContext
    | null;

interface Section {
    title?: string;
    key?: string;
    time?: number;
    stimuli_url?: string;
    stimulusImages?: any[];
    stimulusMedia?: any[];
    questions: string[];
    probingNotes?: (ProbingNote | null)[];
    UID?: string;
    suggested_probes?: string[][];
    addVision?: boolean;
    visualAnalysis?: { enabled?: boolean; [key: string]: any };
    quant?: { question: string; config: QuantConfig };
}

interface DiscussionGuide {
    introduction: { questions: string[]; UID?: string };
    mainSections: Section[];
    conclusion: { questions: string[]; UID?: string };
}

@Injectable({
    providedIn: 'root',
})
export class AiModeratorService {
    fakeMic: any;
    destination: any;
    ws: WebSocket | undefined;
    scheduledAudioSources: any = [];
    audioContextOut: any;
    startTime: number = -1;
    intro: string = '';
    conversation: Array<any> = [];
    transcript: string = '';
    transcriptTimestamp: string = '';
    reconnectAttempts: number = 0;
    maxReconnectAttempts: number = 5;
    // Handle for the reconnect backoff timer so it can be cancelled on teardown.
    // An uncancelled straggler fired after end/leave and reopened a zombie WS.
    private reconnectTimer: any;
    // The local audio track can be momentarily unavailable right after a WS
    // reconnect. Retry acquiring it a few times before giving up so a transient
    // race self-heals instead of leaving the agent deaf for the rest of the
    // interview.
    audioTrackRetryAttempts: number = 0;
    maxAudioTrackRetryAttempts: number = 5;
    audioTrackRetryDelayMs: number = 300;
    audioTrackRetryTimer: any = null;
    connectionAttemptTimestamps: number[] = [];
    maxConnectionAttempts: number = 5;
    connectionAttemptWindowMinutes: number = 5;
    // Intentionally left empty — see processAudioQueue(). Audio chunks are
    // streamed to the provider and must not be buffered in memory.
    recordedAudio: Array<any> = [];
    feedbackItems: Array<any> = [];

    rawDiscussionGuide: any;
    completedTopics: {
        topicUID: string;
        topicTitle: string;
        questionsAnswered: number;
        completionNotes?: string;
        timestamp: number;
    }[] = [];

    completedTopicsTimestamps: {
        topicUID: string;
        topicTitle: string;
        startTime: number;
        questionsAnswered?: number;
        endTime?: number;
        originalTime?: number;
    }[] = [];

    // Progress through the interview's topics, surfaced to the participant as a
    // progress bar / "X out of N topics completed". Every section that has a UID
    // is counted — the introduction and conclusion included — so completing the
    // introduction advances the count just like a main section. Recomputed when
    // the guide is set and after each topic is marked completed.
    topicProgress$ = new BehaviorSubject<{ completed: number; total: number }>({
        completed: 0,
        total: 0,
    });

    // Video analytics storage
    videoAnalytics: {
        sessions: any[];
        aggregateMetrics: any;
        topicVideoEngagement: { [topicId: string]: any };
    } = {
        sessions: [],
        aggregateMetrics: null,
        topicVideoEngagement: {},
    };

    // Audio analytics storage. Kept separate from videoAnalytics so the video
    // figures reported for an interview keep their existing meaning.
    audioAnalytics: {
        sessions: any[];
        aggregateMetrics: any;
        topicAudioEngagement: { [topicId: string]: any };
    } = {
        sessions: [],
        aggregateMetrics: null,
        topicAudioEngagement: {},
    };

    private _provider: 'daily' | 'openai' | 'elevenlabs' = 'elevenlabs';

    private _ttsVoice: string = 'aura-2-delia-en';

    private audioContext: AudioContext | null = null;
    private workletNode: AudioWorkletNode | null = null;

    private audioBufferQueue: Int16Array[] = [];
    private isSending = false;
    // Accumulation buffer for the worklet's tiny chunks (~128 input frames,
    // i.e. a few ms each) so mic audio goes out as ~100ms blocks. Without
    // this, every worklet quantum became its own JSON+base64 WebSocket send —
    // hundreds of sends per second of main-thread work. Mirrors the
    // accumulator the caption service already uses.
    private accumulatedSamples: Int16Array[] = [];
    private accumulatedLength = 0;
    // ~100ms of audio at ElevenLabs' 16kHz input rate.
    private readonly SEND_THRESHOLD_SAMPLES = 1600;
    // Edge-trigger flag so the "buffer full" warning is logged once per
    // saturation episode instead of every ~50ms while the buffer stays full.
    private bufferFullWarned = false;

    private _lastInterruptEventId: number = 0;

    aiModerator = new BehaviorSubject(false);
    devices = new BehaviorSubject<any>({});

    callObject: DailyCall | undefined;

    discussionGuide: any;
    unsubscribe$ = new Subject();

    private _moderatorRoomId: string = '';
    private _audioDelayEnabled: boolean = false;
    private elevenLabsConversation: Conversation | null = null;
    private _aiModeratorProjectAgentId: string = '';
    // The agent id actually used to mint the current signed URL. Flag-agent
    // rooms have no ai-moderator-project-agent-id, so aiModeratorProjectAgentId
    // is empty and cannot re-mint on reconnect; this preserves the agent id
    // that produced the working URL so the reconnect can re-mint against it.
    private _signedUrlAgentId: string = '';

    public get moderatorRoomId(): string {
        return this._moderatorRoomId;
    }

    private _aiPersonality: string = '';

    get aiPersonality() {
        return this._aiPersonality;
    }

    set aiPersonality(personality: string) {
        this._aiPersonality = personality;
    }

    get aiModeratorProjectAgentId() {
        return this._aiModeratorProjectAgentId;
    }

    set aiModeratorProjectAgentId(agentId: string) {
        this._aiModeratorProjectAgentId = agentId;
    }

    get signedUrlAgentId() {
        return this._signedUrlAgentId;
    }

    set signedUrlAgentId(agentId: string) {
        this._signedUrlAgentId = agentId;
    }

    get provider() {
        return this._provider;
    }

    get ttsVoice() {
        return this._ttsVoice;
    }

    get audioDelay() {
        return this._audioDelayEnabled;
    }

    get isMicEnabled(): boolean {
        return !!this.participantService.micEnabled;
    }

    get elevenLabsSettings() {
        let language = this.getLanguage();

        let prompt = this.intro + this.discussionGuide;

        if (this.participantService.localParticipant?.userName) {
            prompt +=
                ' The person you are interviewing is called ' +
                this.participantService.localParticipant?.userName +
                '.';
        }

        if (this.visualAnalysisService.isEnabled()) {
            prompt +=
                '\n\n### VISUAL OBSERVATION INSTRUCTIONS\n' +
                'You will periodically receive VISUAL OBSERVATION contextual updates from a real-time ' +
                "video analysis system. These observations describe what the participant is doing " +
                '(via webcam) or showing (via screen share).\n\n' +
                'Guidelines for using visual observations:\n' +
                '- If an observation notes confusion, difficulty, or frustration, naturally ask about it. ' +
                'For example: "I noticed you seemed to pause there — what were you thinking?"\n' +
                '- If an observation notes the participant found or reacted to something, follow up naturally. ' +
                'For example: "It looks like you found that feature — what do you think of it?"\n' +
                '- Do NOT explicitly tell the participant you are watching their screen or webcam. ' +
                'Frame questions as natural follow-ups.\n' +
                '- Do NOT interrupt the participant mid-sentence to comment on visual observations. ' +
                'Wait for natural pauses.\n' +
                '- Use LOW significance observations as background context only — do not ask about them.\n' +
                '- Use MEDIUM and HIGH significance observations to inform your follow-up questions.\n' +
                '- VISUAL ANALYSIS SUMMARY observations arrive at the end of a topic — use them ' +
                'to ask any final questions before moving on.\n';
        }

        let voiceId = 'DXFkLCBUTmvXpp2QwZjA';

        if (this.ttsVoice && this.elVoices.includes(this.ttsVoice)) {
            voiceId = this.ttsVoice;
        }

        let settings: any = {
            type: 'conversation_initiation_client_data',
            conversation_config_override: {
                agent: {
                    prompt: {
                        prompt: prompt,
                    },
                    language: language,
                },
                tts: {
                    voice_id: voiceId,
                },
            },
        };

        if (this.aiModeratorProjectAgentId) {
            settings.conversation_config_override = {
                agent: {
                    language: language,
                },
                tts: {
                    voice_id: voiceId,
                },
            };
        }

        if (this._conversationId) {
            settings.dynamic_variables = {
                previous_conversation_id: this._conversationId,
            };

            if (this.aiModeratorProjectAgentId) {
                // Build a first_message that tells the agent where to continue
                settings.conversation_config_override.agent.first_message =
                    this.buildReconnectFirstMessage();

                // Drive the workflow's resume_router to the in-progress topic node
                // (and seed that topic's content) so the agent continues from where
                // we left off instead of restarting the discussion guide. Agents
                // without the router simply ignore these variables (backwards
                // compatible — see previous_conversation_id, also undeclared).
                const resumeVars = this.buildResumeRoutingVariables();
                if (resumeVars) {
                    settings.dynamic_variables = {
                        ...settings.dynamic_variables,
                        ...resumeVars,
                    };
                } else {
                    // Reconnect before any topic completed: there is nothing to
                    // resume into, so the resume_router falls through to the
                    // introduction. The intro's markTopicCompleted2 is a workflow
                    // tool node whose UID param is dynamic_variable-bound to
                    // next_topic_uid; nothing has populated it yet, so it cannot
                    // resolve and the call fails with "Failed to prepare client
                    // tool parameters". Seed the introduction UID so the intro
                    // completion resolves deterministically (mirrors the fresh
                    // start branch below)
                    const introUID = this.getIntroductionUID();
                    if (introUID) {
                        settings.dynamic_variables = {
                            ...settings.dynamic_variables,
                            next_topic_uid: introUID,
                        };
                    }
                }
            } else if (this.conversation.length > 0) {
                settings.conversation_config_override.agent.prompt.prompt +=
                    ' This is the continuation of an existing conversation. I have provided the conversation in json stringified format: ' +
                    JSON.stringify(this.conversation) +
                    'please continue from where we left off.';

                settings.conversation_config_override.agent.first_message =
                    'Sorry it looks like we got disconnected, would you like me to repeat the last question?';
            }
        } else if (this.aiModeratorProjectAgentId) {
            // Fresh start with the workflow agent: the introduction's
            // markTopicCompleted2 is a workflow tool node that fires before any tool
            // response has populated next_topic_uid. Nothing else seeds it on a fresh
            // start, so a dynamic_variable-bound UID param cannot resolve and the call
            // fails with "Failed to prepare client tool parameters". Seed the
            // introduction UID up front so the intro completion resolves
            // deterministically (mirrors buildResumeRoutingVariables() on reconnect)
            const introUID = this.getIntroductionUID();
            if (introUID) {
                settings.dynamic_variables = {
                    ...settings.dynamic_variables,
                    next_topic_uid: introUID,
                };
            }
        }

        return settings;
    }

    /**
     * Returns the introduction section's UID from the discussion guide, or null
     * when there is no guide / no introduction UID. Used to seed next_topic_uid on
     * a fresh start so the introduction's markTopicCompleted2 can resolve its UID
     * deterministically.
     */
    private getIntroductionUID(): string | null {
        if (!this.rawDiscussionGuide) {
            return null;
        }
        try {
            const guide = this.parseDiscussionData(
                JSON.parse(this.rawDiscussionGuide),
            );
            return guide.introduction?.UID || null;
        } catch (e) {
            console.error('Error determining introduction UID:', e);
            return null;
        }
    }

    get isAiModerator() {
        return !!this.aiModerator?.value;
    }

    get locale(): string {
        return $localize.locale ?? 'en';
    }

    getCompletedTopics() {
        return this.completedTopics;
    }

    /**
     * Recompute progress through the interview's topics and emit it on
     * topicProgress$. Total is the number of sections in the guide that have a
     * UID — introduction, main sections and conclusion all count; completed is
     * how many of those UIDs appear in completedTopics. Sections without a UID
     * (e.g. legacy array-form guides) cannot be completed, so they are not
     * counted. Safe to call before a guide is set — it emits
     * {completed: 0, total: 0} in that case.
     */
    updateTopicProgress(): void {
        let total = 0;
        let completed = 0;

        if (this.rawDiscussionGuide) {
            try {
                const guide = this.parseDiscussionData(
                    JSON.parse(this.rawDiscussionGuide),
                );

                const topicUIDs = [
                    guide.introduction.UID,
                    ...guide.mainSections.map((section) => section.UID),
                    guide.conclusion.UID,
                ].filter((uid): uid is string => !!uid);

                total = topicUIDs.length;

                const completedUIDs = new Set(
                    this.completedTopics.map((topic) => topic.topicUID),
                );

                completed = topicUIDs.filter((uid) =>
                    completedUIDs.has(uid),
                ).length;
            } catch (e) {
                console.error('Error computing topic progress:', e);
            }
        }

        this.topicProgress$.next({ completed, total });

        // Progress just changed — persist it so a mid-interview page reload can
        // resume rather than restart at the introduction.
        this.persistResumeState();
    }

    // sessionStorage key for this room's resume state. sessionStorage (not
    // local) so it is scoped to the tab and cleared when the tab closes.
    private static readonly RESUME_STORAGE_PREFIX = 'vpm_ai_resume_';

    private getResumeStorageKey(): string | null {
        const roomId = this.moderatorRoomId || this.roomService.roomId;
        return roomId
            ? AiModeratorService.RESUME_STORAGE_PREFIX + roomId
            : null;
    }

    /**
     * Persist the in-progress interview state (completed topics, started-topic
     * timestamps, ElevenLabs conversation id and the analytics user id) so a
     * full page reload can resume the interview instead of restarting it. Only
     * writes once a conversation actually exists — before that there is nothing
     * to resume and we must not shadow a genuine fresh start. Best-effort:
     * sessionStorage can throw (Safari private mode) and a failure to persist
     * must never break the interview.
     */
    private persistResumeState(): void {
        if (!this._conversationId) {
            return;
        }
        const key = this.getResumeStorageKey();
        if (!key) {
            return;
        }
        try {
            sessionStorage.setItem(
                key,
                JSON.stringify({
                    completedTopics: this.completedTopics,
                    completedTopicsTimestamps: this.completedTopicsTimestamps,
                    conversationId: this._conversationId,
                    userId: this._userId,
                }),
            );
        } catch (e) {
            console.warn('Failed to persist AI resume state', e);
        }
    }

    /**
     * Restore interview progress persisted by persistResumeState() for this
     * room, if any. Called from setAiDiscussionGuide() after the guide is set.
     * No-op when there is no saved state (a fresh interview). Best-effort.
     */
    private restoreResumeState(): void {
        const key = this.getResumeStorageKey();
        if (!key) {
            return;
        }
        try {
            const raw = sessionStorage.getItem(key);
            if (!raw) {
                return;
            }
            const saved = JSON.parse(raw);
            if (!saved?.conversationId) {
                return;
            }
            this.completedTopics = Array.isArray(saved.completedTopics)
                ? saved.completedTopics
                : [];
            this.completedTopicsTimestamps = Array.isArray(
                saved.completedTopicsTimestamps,
            )
                ? saved.completedTopicsTimestamps
                : [];
            this._conversationId = saved.conversationId;
            if (saved.userId) {
                this._userId = saved.userId;
            }
            console.log(
                `Restored AI resume state: ${this.completedTopics.length} completed topic(s), conversation ${this._conversationId}.`,
            );
        } catch (e) {
            console.warn('Failed to restore AI resume state', e);
        }
    }

    /** Clear persisted resume state — called when the interview ends. */
    private clearResumeState(): void {
        const key = this.getResumeStorageKey();
        if (!key) {
            return;
        }
        try {
            sessionStorage.removeItem(key);
        } catch (e) {
            console.warn('Failed to clear AI resume state', e);
        }
    }

    getStartedTopics() {
        return this.completedTopicsTimestamps;
    }

    getTopicUIDByTitle(topicTitle: string): string | null {
        if (!this.rawDiscussionGuide) {
            return null;
        }

        try {
            const discussionData = JSON.parse(this.rawDiscussionGuide);

            // Find all sections with the matching title
            const matchingSections = discussionData.filter(
                (item: any) => item.title === topicTitle,
            );

            if (matchingSections.length === 0) {
                return null;
            }

            // If only one section with this title exists, return its UID
            if (matchingSections.length === 1) {
                return matchingSections[0]?.UID || null;
            }

            // For duplicate titles, find the next unstarted topic
            // Get UIDs of topics with this title that have already been started
            const startedTopicUIDs = this.completedTopicsTimestamps
                .filter((topic) => topic.topicTitle === topicTitle)
                .map((topic) => topic.topicUID);

            // Find the first section with this title that hasn't been started yet
            const nextUnstartedSection = matchingSections.find(
                (section: any) => !startedTopicUIDs.includes(section.UID),
            );

            return nextUnstartedSection?.UID || null;
        } catch (e) {
            console.error('Error finding topic UID:', e);
            return null;
        }
    }

    getTopicByUID(topicUID: string): Section | null {
        if (!this.rawDiscussionGuide) {
            return null;
        }

        try {
            const discussionData = JSON.parse(this.rawDiscussionGuide);

            // Check all sections including intro and conclusion
            const section = discussionData.find(
                (item: any) => item.UID === topicUID,
            );

            return section || null;
        } catch (e) {
            console.error('Error finding topic by UID:', e);
            return null;
        }
    }

    getStimulusForTopic(topicUID: string): {
        stimuli_url?: string;
        stimulusImages?: any[];
        stimulusMedia?: any[];
    } | null {
        if (!this.rawDiscussionGuide) {
            return null;
        }

        try {
            const discussionData = JSON.parse(this.rawDiscussionGuide);
            const guide = this.parseDiscussionData(discussionData);

            // Find the section that matches the topic UID
            const section = guide.mainSections.find((section) => {
                return section.UID === topicUID;
            });

            if (!section) return null;

            // Handle new unified stimulusMedia structure
            if (section.stimulusMedia && section.stimulusMedia.length > 0) {
                // Separate images for backwards compatibility
                const images = section.stimulusMedia.filter(
                    (media: any) => media.type === 'image',
                );

                return {
                    stimulusMedia: section.stimulusMedia,
                    stimulusImages: images.length > 0 ? images : undefined,
                };
            }

            // Fallback to old format for backwards compatibility
            const result: { stimuli_url?: string; stimulusImages?: any[] } = {};
            if (section.stimuli_url) {
                result.stimuli_url = section.stimuli_url;
            }
            if (section.stimulusImages) {
                result.stimulusImages = section.stimulusImages;
            }

            return Object.keys(result).length > 0 ? result : null;
        } catch (e) {
            console.error('Error finding stimulus for topic:', e);
            return null;
        }
    }

    /**
     * Get simplified stimulus info for a topic (for AI tool responses)
     * @returns { has_stimulus: boolean, type: 'images' | 'video' | 'audio' | null }
     */
    getStimulusInfo(topicUID: string): { has_stimulus: boolean; type: 'images' | 'video' | 'audio' | null } {
        const stimulusData = this.getStimulusForTopic(topicUID);

        if (!stimulusData) {
            return { has_stimulus: false, type: null };
        }

        // Check for video in stimulusMedia
        if (stimulusData.stimulusMedia && stimulusData.stimulusMedia.length > 0) {
            const hasVideo = stimulusData.stimulusMedia.some(
                (media: any) => media.type === 'video'
            );
            if (hasVideo) {
                return { has_stimulus: true, type: 'video' };
            }
            // Audio-only topics reported 'images' before, which had the agent
            // referring to an image the participant was never shown. Images keep
            // priority when a topic carries both.
            const hasImage = stimulusData.stimulusMedia.some(
                (media: any) => media.type === 'image'
            );
            if (!hasImage) {
                const hasAudio = stimulusData.stimulusMedia.some(
                    (media: any) => media.type === 'audio'
                );
                if (hasAudio) {
                    return { has_stimulus: true, type: 'audio' };
                }
            }
            return { has_stimulus: true, type: 'images' };
        }

        // Fallback to old format - check for stimuli_url (video) or stimulusImages
        if (stimulusData.stimuli_url) {
            return { has_stimulus: true, type: 'video' };
        }
        if (stimulusData.stimulusImages && stimulusData.stimulusImages.length > 0) {
            return { has_stimulus: true, type: 'images' };
        }

        return { has_stimulus: false, type: null };
    }

    areAllTopicsCompleted(): boolean {
        if (!this.rawDiscussionGuide) {
            return false;
        }

        try {
            const discussionData = JSON.parse(this.rawDiscussionGuide);
            const guide = this.parseDiscussionData(discussionData);

            const completedTopicUIDs = this.completedTopics.map(
                (topic) => topic.topicUID,
            );

            const allMainSectionsCompleted = guide.mainSections.every(
                (section) => {
                    return (
                        section.UID && completedTopicUIDs.includes(section.UID)
                    );
                },
            );

            // For introduction and conclusion, we need to find their UIDs
            const introSection = discussionData.find(
                (item: any) => item.title === 'Introduction',
            );
            const conclusionSection = discussionData.find(
                (item: any) => item.title === 'Conclusion',
            );

            // Introduction and conclusion are required for a complete discussion
            const hasIntroCompletion = introSection
                ? completedTopicUIDs.includes(introSection.UID)
                : false;
            const hasConclusionCompletion = conclusionSection
                ? completedTopicUIDs.includes(conclusionSection.UID)
                : false;

            return (
                allMainSectionsCompleted &&
                hasIntroCompletion &&
                hasConclusionCompletion
            );
        } catch (e) {
            console.error('Error checking topic completion status:', e);
            return false;
        }
    }

    private _signedUrl: string = '';
    private _language: string = 'en';

    private _hasAiSpokenOnce = false;

    // Add properties for tracking response time
    private _firstAgentAudioDoneTimestamp: number | null = null;
    private _responseTimeTracked = false;
    private _welcomeTimestamp: number | null = null;
    private _welcomeResponseTimeTracked = false;

    // Add property for tracking interview duration
    private _interviewStartTimestamp: number | null = null;
    private _userId: string = '';

    private _sampleRate: number = 24000; // Default sample rate

    private _conversationId: string | null = null;

    // Store original query string for page reload on connection failure
    private originalQueryString: string = '';

    // Stimulus activity tracking
    private _stimulusActivityInterval: any = null;

    // Add new Subject for AI captions
    aiCaptionsSubject = new BehaviorSubject<{
        text: string;
        user_name: string;
    }>({ text: '', user_name: '' });

    // Add Subject for AI speaking state (used for audio completion detection)
    isAiSpeaking = new BehaviorSubject<boolean>(false);

    // Add new Subject for stimulus display
    stimulusDisplaySubject = new BehaviorSubject<{
        stimuli_url?: string;
        stimulusImages?: any[];
        stimulusMedia?: any[];
        topicTitle: string;
    } | null>(null);

    // Subject for quant question display. Mirrors the stimulus display
    // pattern - next(payload) shows the widget, next(null) hides it.
    quantDisplaySubject = new BehaviorSubject<QuantQuestion | null>(null);

    // Topic UIDs whose quant question has already been shown, so each topic's
    // quant fires once (on its start) even if the topic is revisited.
    private _quantShownTopicUIDs = new Set<string>();
    // Recorded quant answers, sent to the API alongside the transcript.
    quantResponses: Array<{
        question: string;
        format: QuantFormat;
        value: QuantAnswerValue;
        topicTitle: string;
        timestamp: number;
    }> = [];

    private _visualAnalysisService: VisualAnalysisService | null = null;

    private get visualAnalysisService(): VisualAnalysisService {
        if (!this._visualAnalysisService) {
            this._visualAnalysisService = this.injector.get(
                VisualAnalysisService,
            );
        }
        return this._visualAnalysisService;
    }

    // The agent verbally stalls after a show-request when the visual cue has not
    // yet arrived. While the stall window is open the next HIGH-significance
    // webcam observation is routed as a user_message (rather than a silent
    // contextual_update) so ElevenLabs takes a turn even if the participant has
    // gone quiet. Cleared on participant transcript or after a fail-safe timeout.
    private _awaitingPostStallVisual = false;
    private _postStallTimeoutId: any = null;
    private readonly POST_STALL_WINDOW_MS = 15000;

    // Bug 2 ("mid-session stall"): per-turn watchdog. After the participant's
    // turn the AI is expected to respond; if it goes silent (open-but-silent
    // socket, or a clean 1000 close that onclose ignores) the UI is stuck on
    // "Thinking…/Listening…" forever. Recovery is gentle first — nudge the
    // existing connection to prompt a turn — then reconnect only if the nudge
    // also gets no response.
    private _turnWatchdogTimeout: any = null;
    private _turnNudgeTimeout: any = null;
    // Nudge the existing connection after 25s of silence; if the nudge gets no
    // response within a further 15s (40s total), reconnect. The first-nudge
    // threshold is deliberately generous: a healthy-but-slow turn (large
    // discussion guide / RAG latency) that responds around the 15-20s mark
    // used to get nudged with a synthetic user_message and then answer twice
    // — perceived as the AI repeating itself. 25s keeps genuine stalls
    // covered while making that false-nudge case rare.
    private readonly TURN_RESPONSE_TIMEOUT_MS = 25000;
    private readonly TURN_NUDGE_TIMEOUT_MS = 15000;
    // How many nudges we'll send while the connection is provably alive
    // (pings still arriving) before giving up and reconnecting anyway.
    private _turnStallNudgeCount = 0;
    private readonly MAX_STALL_NUDGES = 3;

    // Bug 3 ("no client-side recovery"): connection state the UI surfaces as
    // "Reconnecting…" / "We hit a problem — Rejoin". 'reconnecting' while a
    // reconnect is in flight, 'connected' once the socket reopens, 'failed'
    // when recovery is exhausted.
    connectionState$ = new BehaviorSubject<
        'connected' | 'reconnecting' | 'failed'
    >('connected');

    // Greeting-never-arrived watchdog: armed on connect, cleared on first AI
    // audio; if the AI never greets it forces a reconnect.
    private _greetingWatchdogTimeout: any = null;
    private readonly GREETING_TIMEOUT_MS = 30000;

    // Connection-liveness watchdog (Bug 2 follow-up): ElevenLabs sends a ping
    // heartbeat ~every 2s. Only that ping resets this watchdog; if no ping
    // arrives for LIVENESS_TIMEOUT_MS the connection is dead (or the agent is
    // wedged) even when other frames still flow, so we reconnect. Catches the
    // idle-listening silent drop the turn watchdog can't (no user_transcript).
    private _livenessTimeout: any = null;
    private readonly LIVENESS_TIMEOUT_MS = 30000;
    // Timestamp of the last ping received, so a stalled-turn nudge can tell a
    // provably-alive-but-slow connection apart from a dead one.
    private _lastPingAtMs: number | null = null;

    // Id of the local mic track the uplink graph is currently bound to. Daily
    // can replace the local track on an ICE restart / network reconnect / SFU
    // switch; onLocalAudioTrackChanged rebuilds the graph when this changes so
    // the agent doesn't keep reading a dead track (Bug: stale mic track).
    private _boundAudioTrackId: string | null = null;

    // Distinct close codes so ElevenLabs' termination_reason (and our own
    // ai-ws-closed telemetry) can tell a watchdog close from a network drop
    // instead of everything collapsing into an undifferentiated 1005/1006.
    // 1000 is the only browser-legal "clean" code; 4000-4009 are private-use.
    private readonly WS_CLOSE = {
        NORMAL: 1000,
        CLIENT_RECOVERY: 4000,
        TURN_STALL: 4001,
        LIVENESS_TIMEOUT: 4002,
        GREETING_TIMEOUT: 4003,
        WS_ERROR: 4009,
    } as const;

    // Bound on the reconnect-path signed-URL re-mint (mirrors
    // AI_SIGNED_URL_TIMEOUT_MS in setup.component). The endpoint can hang up
    // to its ~30s gateway limit; without a client timeout a hung re-mint
    // freezes recovery at "Reconnecting…" forever.
    private readonly SIGNED_URL_REMINT_TIMEOUT_MS = 10000;

    // Backlog #6: tab-resume / network-restore recovery. Background tabs have
    // their timers throttled, and laptop sleep / Wi-Fi switches can silently
    // drop the socket while the watchdogs are frozen — the "random drops
    // around lid-close" reports. When the tab becomes visible again or the
    // network returns, proactively verify the AI socket and reconnect if it's
    // dead or stale instead of waiting for a throttled watchdog to notice.
    private _resumeListenersAdded = false;

    // Set when the interview is intentionally ending (end_call received), so a
    // legitimate server-sent 1000 close isn't treated as an unexpected drop.
    private _endRequested = false;
    // Set when we intentionally leave the bot's own Daily leg, so its resulting
    // 'left-meeting' event isn't misreported as an unexpected bot-drop error.
    private _botLeaveRequested = false;
    private readonly STALL_PHRASE_PATTERNS: RegExp[] = [
        /let me (see|look|have a look|take a look|look a bit closer)/i,
        /i can see you('?ve| have) got something/i,
    ];

    constructor(
        private apiService: ApiService,
        private recordingService: RecordingService,
        private participantService: ParticipantService,
        private userService: UserService,
        private router: Router,
        private roomService: RoomService,
        private videoTrackingService: VideoTrackingService,
        private audioTrackingService: AudioTrackingService,
        private injector: Injector,
        @Inject(LOCALE_ID) public injectedLocale: string,
    ) {
        // Set up initial Sentry context for AI Moderator service
        this.setupSentryContext();
        // Subscribe to video tracking analytics updates
        this.videoTrackingService.analytics$
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((analytics) => {
                if (analytics) {
                    this.videoAnalytics.aggregateMetrics = analytics;
                }
            });
        // Subscribe to audio tracking analytics updates
        this.audioTrackingService.analytics$
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((analytics) => {
                if (analytics) {
                    this.audioAnalytics.aggregateMetrics = analytics;
                }
            });
    }

    ngOnDestroy(): void {
        this.removeResumeListeners();
        this.unsubscribe$.next('');
        this.unsubscribe$.complete();
    }

    private onVisibilityChange = (): void => {
        if (document.visibilityState === 'visible') {
            this.verifyConnectionAfterResume('visibility');
        }
    };

    private onOnline = (): void => {
        this.verifyConnectionAfterResume('online');
    };

    private addResumeListeners(): void {
        if (this._resumeListenersAdded) {
            return;
        }
        this._resumeListenersAdded = true;
        document.addEventListener('visibilitychange', this.onVisibilityChange);
        window.addEventListener('online', this.onOnline);
    }

    private removeResumeListeners(): void {
        if (!this._resumeListenersAdded) {
            return;
        }
        this._resumeListenersAdded = false;
        document.removeEventListener(
            'visibilitychange',
            this.onVisibilityChange,
        );
        window.removeEventListener('online', this.onOnline);
    }

    /**
     * Called when the tab becomes visible again or the network is restored.
     * If an AI session is active but its socket is closed or has gone stale
     * (no ping within the liveness window), force a reconnect immediately
     * rather than waiting for a background-throttled watchdog to catch it.
     */
    private verifyConnectionAfterResume(source: 'visibility' | 'online'): void {
        if (this._endRequested || this.callEnding || !this.isAiModerator) {
            return;
        }
        // A reconnect is already in flight — let it run.
        if (this.connectionState$.value === 'reconnecting') {
            return;
        }

        const socketOpen = this.isWebSocketOpen();
        const socketStale =
            socketOpen &&
            this._lastPingAtMs !== null &&
            !this.isConnectionLive();

        if (socketOpen && !socketStale) {
            return; // Connection looks healthy — nothing to do.
        }

        console.warn(
            `AI moderator: ${source} resume — socket ${
                socketOpen ? 'stale' : 'closed'
            }, forcing reconnect.`,
        );
        LogRocket.track('ai-resume-reconnect', { source });

        // The participant is actively back — give recovery a fresh budget so a
        // prior exhausted/failed loop doesn't leave them stuck.
        this.reconnectAttempts = 0;
        this.connectionState$.next('reconnecting');
        this.closeWebSocket(
            true,
            this.WS_CLOSE.CLIENT_RECOVERY,
            `${source}-resume`,
        );
    }

    private setupSentryContext(): void {
        // Set initial Sentry context and tags for AI Moderator service
        Sentry.setContext('ai_moderator_service', {
            provider: this._provider,
            locale: this.locale,
            audioDelayEnabled: this._audioDelayEnabled,
            sampleRate: this._sampleRate,
        });

        Sentry.setTag('service', 'ai-moderator');
        Sentry.setTag('provider', this._provider);
        Sentry.setTag('locale', this.locale);

        Sentry.addBreadcrumb({
            message: 'AI Moderator Service initialized',
            category: 'service.init',
            level: 'info',
            data: {
                provider: this._provider,
                locale: this.locale,
            },
        });
    }

    setProvider(provider: 'daily' | 'openai' | 'elevenlabs') {
        const previousProvider = this._provider;
        this._provider = provider;

        // Update Sentry context when provider changes
        Sentry.setTag('provider', provider);
        Sentry.setContext('ai_moderator_service', {
            provider: provider,
            locale: this.locale,
            audioDelayEnabled: this._audioDelayEnabled,
            sampleRate: this._sampleRate,
        });

        Sentry.addBreadcrumb({
            message: 'AI Moderator provider changed',
            category: 'service.config',
            level: 'info',
            data: {
                previousProvider: previousProvider,
                newProvider: provider,
            },
        });
    }

    setAudioDelay(b: boolean) {
        this._audioDelayEnabled = b;
    }

    setInterviewStartTime() {
        if (this._interviewStartTimestamp === null) {
            this._interviewStartTimestamp = Date.now();
        }
    }

    getSignedUrl(): string {
        if (this._signedUrl) {
            return this._signedUrl;
        }

        return '';
    }

    getSampleRate(): number {
        return this._sampleRate;
    }

    setSampleRate(rate: number) {
        this._sampleRate = rate;
    }

    setSignedUrl(signedUrl: string) {
        this._signedUrl = signedUrl;
    }

    getLanguage(): string {
        // Map locale codes to ElevenLabs supported languages for conversational AI
        const localeToLanguageMap: { [key: string]: string } = {
            ae: 'ar', // UAE - Arabic (ElevenLabs: ar - Saudi Arabia/UAE)
            ar: 'es', // Argentina - Spanish (ElevenLabs: es - Spain/Mexico)
            at: 'de', // Austria - German (ElevenLabs: de - reuses Germany)
            aus: 'en', // Australia - English (ElevenLabs: en - includes Australian accent)
            be: 'nl', // Belgium - Dutch (ElevenLabs: nl - reuses Netherlands)
            br: 'pt-br', // Brazil - Portuguese (ElevenLabs: pt - Brazil/Portugal)
            'ca-fr': 'fr', // Canada French - French (ElevenLabs: fr - includes Canadian)
            'ca-en': 'en', // Canada English - English (ElevenLabs: en - includes Canadian accent)
            ch: 'de', // Switzerland - German (ElevenLabs: de - reuses Germany)
            cl: 'es', // Chile - Spanish (ElevenLabs: es - reuses Mexico Spanish)
            co: 'es', // Colombia - Spanish (ElevenLabs: es - Spain/Mexico)
            cz: 'cs', // Czech Republic - Czech (ElevenLabs: cs)
            de: 'de', // Germany - German (ElevenLabs: de)
            eg: 'ar', // Egypt - Arabic (ElevenLabs: ar - Saudi Arabia/UAE)
            es: 'es', // Spain - Spanish (ElevenLabs: es - Spain/Mexico)
            fr: 'fr', // France - French (ElevenLabs: fr - France/Canadian)
            gr: 'el', // Greece - Greek (ElevenLabs: el)
            hr: 'hr', // Croatia - Croatian (ElevenLabs: hr)
            hu: 'hu', // Hungary - Hungarian (ElevenLabs: hu - not on Multilingual v2)
            id: 'id', // Indonesia - Indonesian (ElevenLabs: id)
            ie: 'en', // Ireland - English (ElevenLabs: en)
            in: 'hi', // India - Hindi (ElevenLabs: hi)
            jm: 'en', // Jamaica - English (ElevenLabs: en)
            jp: 'ja', // Japan - Japanese (ElevenLabs: ja)
            ke: 'en', // Kenya - English fallback (Swahili not supported by ElevenLabs)
            kr: 'ko', // South Korea - Korean (ElevenLabs: ko)
            ma: 'ar', // Morocco - Arabic (ElevenLabs: ar - Saudi Arabia/UAE)
            mx: 'es', // Mexico - Spanish (ElevenLabs: es - Spain/Mexico)
            my: 'ms', // Malaysia - Malay (ElevenLabs: ms)
            ng: 'en', // Nigeria - English (ElevenLabs: en)
            nl: 'nl', // Netherlands - Dutch (ElevenLabs: nl)
            nz: 'en', // New Zealand - English (ElevenLabs: en)
            ph: 'fil', // Philippines - Filipino (ElevenLabs: fil)
            pl: 'pl', // Poland - Polish (ElevenLabs: pl)
            pt: 'pt', // Portugal - Portuguese (ElevenLabs: pt)
            ro: 'ro', // Romania - Romanian (ElevenLabs: ro)
            sa: 'ar', // Saudi Arabia - Arabic (ElevenLabs: ar - Saudi Arabia/UAE)
            se: 'sv', // Sweden - Swedish (ElevenLabs: sv)
            si: 'sl', // Slovenia - Slovenian (ElevenLabs: sl - v3 and v4 models only)
            it: 'it', // Italy - Italian (ElevenLabs: it)
            uk: 'en', // United Kingdom - English (ElevenLabs: en - includes UK accent)
            us: 'en', // United States - English (ElevenLabs: en - includes USA accent)
            vn: 'vi', // Vietnam - Vietnamese (ElevenLabs: vi - not on Multilingual v2)
            za: 'en', // South Africa - English fallback (Afrikaans not supported by ElevenLabs)
            th: 'th', // Thailand - Thai (ElevenLabs: th - v3 and v4 models only)
            tr: 'tr', // Turkey - Turkish (ElevenLabs: tr)
            en: 'en', // Default English (ElevenLabs: en)
        };

        const currentLocale = this.locale;

        return localeToLanguageMap[currentLocale] || 'en';
    }

    setLanguage(language: string) {
        this._language = language;
    }

    setOriginalQueryString(queryString: string) {
        this.originalQueryString = queryString;
    }

    private elVoices: string[] = [
        'DXFkLCBUTmvXpp2QwZjA',
        'E5vwBa1swCEXshQFkLEu',
        'uYXf8XasLslADfZ2MB4u',
        'gfRt6Z3Z8aTbpLfexQ7N',
        'ImnfuV8oxhB7ya99oJfc',
        'G17SuINrv2H9FC6nvetn',
        '8N2ng9i2uiUWqstgmWlH',
        'jRAAK67SEFE9m7ci5DhD',
        // Legacy voices retained for back-compat with older projects
        'UgBBYS2sOqTuMpoF3BR0',
        'g6xIsTj2HwM6VR4iXFCw',
        'kmSVBPu7loj4ayNinwWM',
        'ZtcPZrt9K4w8e1OB9M6w',
    ];

    setTTSVoice(
        ttsVoice:
            | 'DXFkLCBUTmvXpp2QwZjA'
            | 'E5vwBa1swCEXshQFkLEu'
            | 'uYXf8XasLslADfZ2MB4u'
            | 'gfRt6Z3Z8aTbpLfexQ7N'
            | 'ImnfuV8oxhB7ya99oJfc'
            | 'G17SuINrv2H9FC6nvetn'
            | '8N2ng9i2uiUWqstgmWlH'
            | 'jRAAK67SEFE9m7ci5DhD'
            // Legacy voices retained for back-compat with older projects
            | 'UgBBYS2sOqTuMpoF3BR0'
            | 'g6xIsTj2HwM6VR4iXFCw'
            | 'kmSVBPu7loj4ayNinwWM'
            | 'ZtcPZrt9K4w8e1OB9M6w',
    ) {
        this._ttsVoice = ttsVoice;
    }

    setModeratorRoomId(roomId: string) {
        this._moderatorRoomId = roomId;
    }

    setAiDiscussionGuide(discussionGuide: any) {
        // Randomize the discussion guide before storing it
        const randomizedGuide = this.randomizeDiscussionGuide(discussionGuide);
        this.rawDiscussionGuide = JSON.stringify(randomizedGuide);
        this.discussionGuide =
            typeof randomizedGuide === 'string'
                ? randomizedGuide
                : this.formatDiscussion(randomizedGuide);

        this._userId = crypto.randomUUID();

        // Resume-after-reload: a WebSocket reconnect keeps this singleton's
        // in-memory progress, but a full page reload destroys it and the
        // interview would restart from the introduction. Restore any progress
        // persisted for this room (completed topics, conversation id, user id)
        // so the reconnect resume path can continue where we left off. Runs
        // after the guide is set (UIDs need the guide) and before the initial
        // progress emit below. No-op on a genuine fresh start.
        this.restoreResumeState();

        LogRocket.track('vpm_user_id', { userId: this._userId });

        // Seed the topic progress now that we know the total number of topics.
        this.updateTopicProgress();

        // Track if discussion guide has stimulus images
        let hasStimulusImages = false;

        if (discussionGuide && Array.isArray(discussionGuide)) {
            hasStimulusImages = discussionGuide.some(
                (section: any) =>
                    section.stimulusImages &&
                    Array.isArray(section.stimulusImages) &&
                    section.stimulusImages.length > 0,
            );
        }

        if (hasStimulusImages) {
            LogRocket.track('discussion_guide_has_stimulus_images', {
                hasStimulusImages: true,
            });
        }
    }

    addFeedback(feedbackData: any) {
        this.feedbackItems.push(feedbackData);

        // Bug 4b: also emit a queryable custom event so "froze / stuck / not
        // responding" reports can be searched across the whole population,
        // rather than only living in the transcript payload. Includes the AI
        // state at report time to help classify never-started vs mid-stall.
        const interviewDurationMinutes =
            this._interviewStartTimestamp !== null
                ? (Date.now() - this._interviewStartTimestamp) / (1000 * 60)
                : undefined;

        LogRocket.track('trouble-report', {
            text: feedbackData?.feedback ?? '',
            interviewDurationMinutes,
            aiSpeaking: this.isAiSpeaking.value,
            connectionState: this.connectionState$.value,
            hasAiSpokenOnce: this._hasAiSpokenOnce,
        });
    }

    /**
     * Closes the AI playback AudioContext if one exists. AudioContexts are a
     * limited browser resource; without this they accumulate across
     * sessions/reconnects until the browser refuses to create more.
     */
    closeAiPlaybackContext(): void {
        const ctx = this.audioContextOut;
        if (ctx && typeof ctx.close === 'function') {
            Promise.resolve(ctx.close()).catch(() => {});
        }
        this.audioContextOut = null;

        // Release the synthetic mic track + destination feeding the bot's audio
        // so they aren't left live after a non-endCall exit (leave / agent-
        // unavailable). setupAiModerator recreates them on the next session.
        try {
            this.fakeMic?.stop?.();
        } catch {
            /* track already ended */
        }
        this.fakeMic = null;
        this.destination = null;
    }

    setupAiModerator(intro: string) {
        this.intro = intro;

        this.closeAiPlaybackContext();
        this.audioContextOut = new window.AudioContext();

        // Browsers (especially mobile/WebView) may create the context in a
        // 'suspended' state until a user gesture. Without resuming it the AI's
        // scheduled audio is never produced and the participant hears silence
        // (perceived as "the AI stopped responding").
        if (this.audioContextOut.state === 'suspended') {
            Promise.resolve(this.audioContextOut.resume()).catch(() => {});
        }

        this.destination = this.audioContextOut.createMediaStreamDestination();

        let mediaStream = this.destination.stream;
        this.fakeMic = mediaStream.getAudioTracks()[0];

        this.recordingService.startRecording();

        // Recover the AI connection when the tab is re-foregrounded or the
        // network returns (background-throttled watchdogs can miss the drop).
        this.addResumeListeners();

        // Set up visual analysis observation subscriptions
        this.setupVisualAnalysisSubscriptions();

        // Bootstrap the AI moderator's Daily leg. Both calls are on the
        // critical path (without a room token + room details the AI never
        // joins, leaving the participant staring at an empty room with
        // recording already running). Previously the inner subscribe had no
        // error handler at all, so a failed/hung room-details fetch failed
        // silently. Retry transient failures, and if bootstrap can't complete
        // route to the agent-unavailable screen instead of a dead room.
        this.apiService
            .getRoomToken(this.moderatorRoomId)
            .pipe(
                timeout({ each: this.SIGNED_URL_REMINT_TIMEOUT_MS }),
                switchMap((data: any) =>
                    this.apiService
                        .getRoomDetailsWithToken(data.token)
                        .pipe(
                            timeout({ each: this.SIGNED_URL_REMINT_TIMEOUT_MS }),
                        ),
                ),
                retry({
                    count: 2,
                    delay: (_error, retryCount) => timer(2000 * retryCount),
                }),
                takeUntil(this.unsubscribe$),
            )
            .subscribe({
                next: (roomData: any) => {
                    this.addAiModeratorToRoom(roomData);
                },
                error: (error: any) => {
                    console.error(
                        'AI moderator bootstrap failed after retries',
                        error,
                    );
                    LogRocket.track('ai-bootstrap-failed', {
                        message: error?.message ?? '',
                    });
                    Sentry.captureException(error, {
                        tags: {
                            component: 'ai-moderator',
                            event_type: 'bootstrap_failed',
                            provider: this._provider,
                        },
                        extra: { moderatorRoomId: this.moderatorRoomId },
                    });
                    this.showAgentUnavailable();
                },
            });
    }

    // Latch so the three visual-analysis subscriptions are created once per page
    // load. setupVisualAnalysisSubscriptions runs from setupAiModerator on every
    // room mount, but this is a root singleton — without the latch each re-entry
    // stacks another set of subs and every observation is sent to ElevenLabs N
    // times (duplicate context + duplicate stall triggers → duplicate AI turns).
    // takeUntil(unsubscribe$) below is a no-op here (never fires in-page).
    private visualSubsInitialized = false;

    private setupVisualAnalysisSubscriptions(): void {
        if (this.visualSubsInitialized) {
            return;
        }
        this.visualSubsInitialized = true;

        // Always subscribe — the feature flag may be enabled after setupAiModerator runs.
        // If visual analysis is disabled, no observations will emit so these are harmless.
        this.visualAnalysisService.immediateObservation$
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((obs) => {
                console.log(
                    'VisualAnalysis: Sending immediate observation to ElevenLabs:',
                    obs.description,
                );
                // Always emit the full observation as silent context.
                this.sendContextualUpdate(
                    this.formatVisualObservation(obs),
                );
                // If we're inside the post-stall window, also push a minimal
                // user_message trigger so EL takes a turn. The data is already
                // in the preceding contextual_update; the trigger carries none.
                if (
                    this._awaitingPostStallVisual &&
                    obs.source === 'webcam'
                ) {
                    console.log(
                        'VisualAnalysis: Pushing stall continuation trigger',
                    );
                    this.sendStallContinuationTrigger('webcam');
                    this.clearAwaitingPostStallVisual();
                }
            });

        this.visualAnalysisService.batchObservations$
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((batch) => {
                if (batch.length > 0) {
                    console.log(
                        `VisualAnalysis: Sending batch of ${batch.length} observations to ElevenLabs`,
                    );
                    this.sendContextualUpdate(
                        this.formatVisualObservationBatch(batch),
                    );
                }
            });

        this.visualAnalysisService.topicSummary$
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(({ topicTitle, summary }) => {
                console.log(
                    'VisualAnalysis: Sending topic summary to ElevenLabs:',
                    summary,
                );
                this.sendContextualUpdate(
                    `[You saw — summary of "${topicTitle}"] ${summary}`,
                );
            });
    }

    private formatVisualObservation(obs: VisualObservation): string {
        const channel = obs.source === 'webcam' ? 'webcam' : 'screen';
        return `[You see — ${channel}] ${obs.description}`;
    }

    private formatVisualObservationBatch(
        observations: VisualObservation[],
    ): string {
        const lines = observations.map((obs) => {
            const channel = obs.source === 'webcam' ? 'webcam' : 'screen';
            return `- [${channel}] ${obs.description}`;
        });
        return `[You see] Recent things you noticed:\n${lines.join('\n')}`;
    }

    private detectVisualStall(text: string): boolean {
        // Only meaningful while a vision-enabled topic is actively running.
        // Outside that window (idle / paused) there is no observation to break
        // a stall with, so don't even run the regex.
        if (this.visualAnalysisService.state$.value !== 'active') {
            return false;
        }
        if (!text) return false;
        return this.STALL_PHRASE_PATTERNS.some((p) => p.test(text));
    }

    private setAwaitingPostStallVisual(): void {
        this._awaitingPostStallVisual = true;
        if (this._postStallTimeoutId) {
            clearTimeout(this._postStallTimeoutId);
        }
        this._postStallTimeoutId = setTimeout(() => {
            this._awaitingPostStallVisual = false;
            this._postStallTimeoutId = null;
        }, this.POST_STALL_WINDOW_MS);
    }

    private clearAwaitingPostStallVisual(): void {
        this._awaitingPostStallVisual = false;
        if (this._postStallTimeoutId) {
            clearTimeout(this._postStallTimeoutId);
            this._postStallTimeoutId = null;
        }
    }

    // Start (or restart) the per-turn watchdog after the participant speaks.
    // If the AI produces no response within TURN_RESPONSE_TIMEOUT_MS the turn
    // is treated as stalled and recovery begins.
    private startTurnWatchdog(): void {
        this.clearTurnWatchdog();
        this._turnWatchdogTimeout = setTimeout(() => {
            this.handleStalledTurn();
        }, this.TURN_RESPONSE_TIMEOUT_MS);
    }

    // Clear the watchdog (and any pending nudge timer). Called when the AI
    // responds, and on WebSocket teardown so it can't fire spuriously.
    clearTurnWatchdog(): void {
        if (this._turnWatchdogTimeout) {
            clearTimeout(this._turnWatchdogTimeout);
            this._turnWatchdogTimeout = null;
        }
        if (this._turnNudgeTimeout) {
            clearTimeout(this._turnNudgeTimeout);
            this._turnNudgeTimeout = null;
        }
        this._turnStallNudgeCount = 0;
    }

    // Arm (or re-arm) the greeting watchdog on connect. If the AI never speaks
    // within GREETING_TIMEOUT_MS, force a reconnect (the never-started case:
    // no user_transcript fires before the first greeting, so the turn watchdog
    // can't cover it).
    private startGreetingWatchdog(): void {
        this.clearGreetingWatchdog();
        if (this._hasAiSpokenOnce) {
            return;
        }
        this._greetingWatchdogTimeout = setTimeout(() => {
            this.handleMissingGreeting();
        }, this.GREETING_TIMEOUT_MS);
    }

    private clearGreetingWatchdog(): void {
        if (this._greetingWatchdogTimeout) {
            clearTimeout(this._greetingWatchdogTimeout);
            this._greetingWatchdogTimeout = null;
        }
    }

    // Reset on each ElevenLabs ping (the connection's ~2s heartbeat). Called
    // only from the ping handler, not on every inbound frame.
    private resetLivenessWatchdog(): void {
        this._lastPingAtMs = Date.now();
        if (this._livenessTimeout) {
            clearTimeout(this._livenessTimeout);
        }
        this._livenessTimeout = setTimeout(() => {
            this.handleLivenessTimeout();
        }, this.LIVENESS_TIMEOUT_MS);
    }

    // True if a ping has arrived within the liveness window, i.e. the
    // connection is provably alive even if a given turn is slow to respond.
    private isConnectionLive(): boolean {
        return (
            this._lastPingAtMs !== null &&
            Date.now() - this._lastPingAtMs < this.LIVENESS_TIMEOUT_MS
        );
    }

    private clearLivenessWatchdog(): void {
        if (this._livenessTimeout) {
            clearTimeout(this._livenessTimeout);
            this._livenessTimeout = null;
        }
    }

    private handleLivenessTimeout(): void {
        this._livenessTimeout = null;
        if (this._endRequested || this.callEnding) {
            return;
        }

        Sentry.captureMessage('AI moderator WebSocket went silent (no pings)', {
            level: 'warning',
            tags: {
                component: 'ai-moderator',
                event_type: 'ws_liveness_timeout',
                provider: this._provider,
            },
            extra: { conversationId: this._conversationId },
        });

        console.warn(
            'No messages from ElevenLabs within the liveness window; reconnecting.',
        );
        this.connectionState$.next('reconnecting');
        this.closeWebSocket(
            true,
            this.WS_CLOSE.LIVENESS_TIMEOUT,
            'liveness-timeout',
        );
    }

    private handleMissingGreeting(): void {
        this._greetingWatchdogTimeout = null;
        if (this._hasAiSpokenOnce) {
            return;
        }

        Sentry.captureMessage('AI moderator greeting never arrived', {
            level: 'warning',
            tags: {
                component: 'ai-moderator',
                event_type: 'greeting_missing',
                provider: this._provider,
            },
            extra: { conversationId: this._conversationId },
        });

        console.warn(
            'AI greeting never arrived after connect; reconnecting.',
        );
        this.connectionState$.next('reconnecting');
        this.closeWebSocket(
            true,
            this.WS_CLOSE.GREETING_TIMEOUT,
            'greeting-timeout',
        );
    }

    // A turn stalled. Recover gently first: if the socket is still open, nudge
    // it with a user_message to prompt a turn and wait a short window. If the
    // socket is already gone, skip straight to a reconnect.
    private handleStalledTurn(): void {
        this._turnWatchdogTimeout = null;

        Sentry.captureMessage('AI moderator turn stalled', {
            level: 'warning',
            tags: {
                component: 'ai-moderator',
                event_type: 'turn_stalled',
                provider: this._provider,
            },
            extra: {
                conversationId: this._conversationId,
                socketOpen: this.isWebSocketOpen(),
            },
        });

        if (this.provider === 'elevenlabs' && this.isWebSocketOpen()) {
            console.warn(
                'AI turn stalled; nudging the existing connection before reconnecting.',
            );
            this.sendTurnNudge();
        } else {
            console.warn(
                'AI turn stalled and socket not open; reconnecting.',
            );
            this.closeWebSocket(true, this.WS_CLOSE.TURN_STALL, 'turn-stall');
        }
    }

    // Nudge the existing connection with a user_message to prompt a turn. If
    // the nudge window elapses with still no response, only reconnect once
    // the connection is no longer provably alive (pings have also stopped) or
    // we've exhausted MAX_STALL_NUDGES — otherwise a healthy-but-slow turn
    // (e.g. large discussion guide / RAG latency) gets nudged again instead
    // of being dropped into a reconnect that usually fails.
    private sendTurnNudge(): void {
        this._turnStallNudgeCount++;
        this.ws?.send(
            JSON.stringify({
                type: 'user_message',
                text: '[Participant is waiting for a response — please continue.]',
            }),
        );
        this._turnNudgeTimeout = setTimeout(() => {
            this._turnNudgeTimeout = null;
            if (
                this.isConnectionLive() &&
                this._turnStallNudgeCount < this.MAX_STALL_NUDGES
            ) {
                console.warn(
                    'AI turn still stalled after nudge but the connection is alive; nudging again.',
                );
                this.sendTurnNudge();
            } else {
                console.warn(
                    'AI turn still stalled after nudge; reconnecting.',
                );
                this.closeWebSocket(
                    true,
                    this.WS_CLOSE.TURN_STALL,
                    'turn-stall',
                );
            }
        }, this.TURN_NUDGE_TIMEOUT_MS);
    }

    private sendStallContinuationTrigger(
        channel: 'webcam' | 'screen',
    ): void {
        // Minimal user_message that triggers a turn. The observation content
        // itself rides on the contextual_update sent just before; this message
        // carries no payload to avoid duplicating data in context.
        const text = `[Visual context shown — ${channel}]`;
        if (!this.isWebSocketOpen()) {
            console.warn(
                'VisualAnalysis: WebSocket NOT open, stall continuation trigger dropped',
            );
            return;
        }
        if (this.provider === 'elevenlabs') {
            this.ws?.send(
                JSON.stringify({
                    type: 'user_message',
                    text,
                }),
            );
        }
    }

    setupWebSocket() {
        // Don't (re)connect once the interview is ending / has ended — a
        // straggler reconnect timer must not resurrect a torn-down session.
        if (this._endRequested || this.callEnding) {
            return;
        }

        Sentry.addBreadcrumb({
            message: 'Setting up WebSocket connection',
            category: 'websocket.setup',
            level: 'info',
            data: {
                provider: this._provider,
                conversationId: this._conversationId,
                reconnectAttempts: this.reconnectAttempts,
            },
        });

        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            console.warn('WebSocket already open, skipping initialization.');
            Sentry.addBreadcrumb({
                message: 'WebSocket setup skipped - already open',
                category: 'websocket.setup',
                level: 'warning',
            });
            return;
        }

        // Check connection attempt limit
        if (!this.canAttemptConnection()) {
            console.error(
                'Maximum connection attempts exceeded. Redirecting to welcome page.',
            );
            Sentry.captureMessage(
                'AI moderator connection attempt limit exceeded',
                {
                    level: 'error',
                    tags: {
                        component: 'ai-moderator',
                        event_type: 'ws_connection_cap',
                        provider: this._provider,
                    },
                    extra: {
                        conversationId: this._conversationId,
                        maxConnectionAttempts: this.maxConnectionAttempts,
                        connectionAttemptWindowMinutes:
                            this.connectionAttemptWindowMinutes,
                    },
                },
            );
            this.showAgentUnavailable();
            return;
        }

        // Track this connection attempt
        this.trackConnectionAttempt();

        let url: string;
        let protocols: string[] | undefined;

        if (this.provider === 'elevenlabs') {
            url = this.getSignedUrl();
        } else {
            console.error(`Unknown provider: ${this.provider}`);
            return;
        }

        // A previous socket that's still CONNECTING/CLOSING (the OPEN case
        // already returned above) would be orphaned by the reassignment below —
        // its onopen would later fire and act on the *new* this.ws. Null its
        // handlers and close it first so nothing acts on a socket we no longer own.
        if (this.ws) {
            this.ws.onopen = null;
            this.ws.onmessage = null;
            this.ws.onerror = null;
            this.ws.onclose = null;
            try {
                this.ws.close();
            } catch {
                /* already closing/closed */
            }
        }

        this.ws = protocols
            ? new WebSocket(url, protocols)
            : new WebSocket(url);
        this.ws.binaryType = 'arraybuffer';
        this.ws.onopen = () => {
            this.reconnectAttempts = 0;
            // Clear the connection-attempt window on every successful open.
            // The cap exists to stop a genuine connect→fail→connect hammer
            // loop; a flaky-but-recovering connection (blip → reconnect →
            // success, a few times over a long interview) previously burned
            // the shared 5-per-5-min budget with its *successful* reconnects
            // and got booted to agent-unavailable mid-interview. Resetting on
            // success means only consecutive failures without any successful
            // open in between count toward the cap.
            this.connectionAttemptTimestamps = [];
            this.connectionState$.next('connected');

            // A fresh connection starts a new ElevenLabs conversation whose
            // audio event_ids restart from low values. Reset the interruption
            // gate so post-reconnect audio isn't discarded as "stale audio due
            // to prior interruption" (which left the AI silent on reconnect
            // while text/CC still came through).
            this._lastInterruptEventId = 0;

            // Arm the greeting watchdog until the AI actually speaks (covers a
            // fresh join and re-arms after each reconnect).
            this.startGreetingWatchdog();

            // Begin watching for connection liveness (silent drops).
            this.resetLivenessWatchdog();

            if (this.provider === 'elevenlabs') {
                this.ws?.send(JSON.stringify(this.elevenLabsSettings));
            }

            this.startStreamingAudioToWebsocket(true);
        };

        this.ws.onmessage = (event: any) => {
            // Liveness is reset by the ping handler only (see the 'ping' case in
            // triggerElevenLabsEvent), not on every frame — otherwise non-ping
            // traffic from a wedged agent keeps the watchdog alive forever.
            if (this.provider === 'elevenlabs') {
                this.triggerElevenLabsEvent(event);
            }
        };

        this.ws.onerror = (error) => {
            console.error('WebSocket error:', error);
            Sentry.captureMessage('AI moderator WebSocket error', {
                level: 'error',
                tags: {
                    component: 'ai-moderator',
                    event_type: 'ws_error',
                    provider: this._provider,
                },
                extra: {
                    conversationId: this._conversationId,
                    reconnectAttempts: this.reconnectAttempts,
                },
            });
            this.closeWebSocket(true, this.WS_CLOSE.WS_ERROR, 'ws-error');
        };

        this.ws.onclose = (event) => {
            console.warn(
                `WebSocket closed (code: ${event.code}, reason: ${event.reason})`,
            );

            // We null onclose before our own intentional closes, so any onclose
            // that fires here is server/network-initiated. Don't reconnect if the
            // interview is legitimately ending.
            if (this._endRequested || this.callEnding) {
                return;
            }

            // Natural end-of-interview close. ElevenLabs can close the socket
            // once the interview has actually finished (a clean 1000, or a 3000
            // "conversation signature invalid/expired" on an auto-reconnect)
            // WITHOUT our client having received an end_call tool event. The
            // generic recovery below would then reconnect into a concluded
            // conversation — rejected with 3000 up to five times — and dump a
            // participant who already finished onto the agent-unavailable
            // screen. If every topic is complete, finalize gracefully (same as
            // the end_call path) instead of reconnecting.
            if (this.areAllTopicsCompleted()) {
                console.log(
                    'AI WebSocket closed after all topics completed; finalizing interview instead of reconnecting.',
                );
                LogRocket.track('ai-ws-closed-after-complete', {
                    code: event.code,
                    reason: event.reason,
                });
                this._endRequested = true;
                this.endCall();
                return;
            }

            // Reconnect on any unexpected close. This now also covers a clean
            // server-sent 1000 mid-conversation (observed: ElevenLabs closing the
            // conversation on a topic transition) which the old code silently
            // accepted, leaving the participant stuck.
            Sentry.captureMessage('AI moderator WebSocket closed abnormally', {
                level: 'warning',
                tags: {
                    component: 'ai-moderator',
                    event_type: 'ws_close',
                    provider: this._provider,
                },
                extra: {
                    closeCode: event.code,
                    closeReason: event.reason,
                    wasClean: event.wasClean,
                    conversationId: this._conversationId,
                    reconnectAttempts: this.reconnectAttempts,
                },
            });

            // Emit a queryable LogRocket event for the WS-close dashboard. Fires
            // only for closes that reach the recovery path (the legit end-of-call
            // guard above returns first), so filtering code != 1000 isolates
            // abnormal closures. Console.warn alone is not reliably queryable.
            LogRocket.track('ai-ws-closed', {
                code: event.code,
                reason: event.reason,
                wasClean: event.wasClean,
            });

            this.closeWebSocket(
                true,
                this.WS_CLOSE.CLIENT_RECOVERY,
                'onclose-reconnect',
            );
        };
    }

    addAiModeratorToRoom(roomData: any) {
        this.callObject = DailyIframe.createCallObject({
            allowMultipleCallInstances: true,
            token: roomData.token,
            sendSettings: {
                video: 'bandwidth-optimized',
                screenVideo: 'detail-optimized',
            },
        });

        // Supervise the bot's own Daily leg. Without this it is the only one
        // of the three connections with no failure handling at all: if it
        // drops, the ElevenLabs WS stays healthy so no watchdog fires, and the
        // participant just hears silence while the AI keeps "conversing".
        this.callObject
            .on('error', (e: any) => this.handleBotCallGone('error', e))
            .on('left-meeting', () =>
                this.handleBotCallGone('left-meeting'),
            );

        this.callObject
            .join({
                userName: 'AI Moderator',
                url: roomData.room_url,
                token: roomData.token,
                startVideoOff: true,
                // startAudioOff: ,

                audioSource: this.fakeMic,
                userData: {
                    isAiModerator: true,
                },
                sendSettings: {
                    video: 'bandwidth-optimized',
                    screenVideo: 'detail-optimized',
                },
            })
            .then(() => {
                this.setupWebSocket();

                if (this.devices.value) {
                    Object.keys(this.devices.value).forEach((key) => {
                        this.participantService.changeMediaDevice(
                            key,
                            this.devices.value[key],
                        );
                    });
                }
            })
            .catch((error: any) => {
                console.error(
                    'AI moderator failed to join Daily room',
                    error,
                );
                Sentry.captureException(error, {
                    tags: {
                        component: 'ai-moderator',
                        event_type: 'daily_join_failed',
                        provider: this._provider,
                    },
                    extra: {
                        conversationId: this._conversationId,
                        roomUrl: roomData?.room_url,
                    },
                });
            });
    }

    // The bot's Daily leg died (fatal error) or left the meeting outside the
    // intentional end-call path. The ElevenLabs WS is likely still healthy, so
    // none of the WS watchdogs will notice — surface it as a failed connection
    // so the Rejoin banner gives the participant a way out instead of silence.
    private handleBotCallGone(
        event: 'error' | 'left-meeting',
        e?: any,
    ): void {
        // _botLeaveRequested covers the intentional leaveBotCall() path (normal
        // participant leave/abandonment), which fires the bot's own
        // 'left-meeting' — that is not an unexpected drop and must not be logged
        // as an error or flip the connection to 'failed'.
        if (this._endRequested || this.callEnding || this._botLeaveRequested) {
            return;
        }

        Sentry.captureMessage('AI moderator Daily leg lost', {
            level: 'error',
            tags: {
                component: 'ai-moderator',
                event_type: 'bot_daily_gone',
                provider: this._provider,
            },
            extra: {
                event: event,
                errorMsg: e?.errorMsg,
                conversationId: this._conversationId,
            },
        });

        LogRocket.track('ai-bot-daily-gone', {
            event: event,
            errorMsg: e?.errorMsg ?? '',
        });

        this.connectionState$.next('failed');
    }

    /**
     * Leave the AI bot's own Daily leg (the second call object this browser
     * hosts). Used when the local participant has left or been removed so the
     * "AI Moderator" doesn't linger in the room as a ghost participant.
     */
    leaveBotCall(): void {
        if (this.callObject) {
            this._botLeaveRequested = true;
            Promise.resolve(this.callObject.leave()).catch(() => {});
        }
    }

    base64ToInt16Array(base64: string): ArrayBuffer {
        const binaryString = atob(base64);
        const len = binaryString.length;
        const bytes = new Uint8Array(len);

        for (let i = 0; i < len; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }

        // Use little-endian interpretation by default (check your PCM format)
        return bytes.buffer;
    }

    int16ArrayToBase64(int16Array: Int16Array): string {
        // Ensure little-endian order
        const buffer = new ArrayBuffer(int16Array.length * 2);
        const view = new DataView(buffer);
        for (let i = 0; i < int16Array.length; i++) {
            view.setInt16(i * 2, int16Array[i], true); // true = little-endian
        }
        // Convert to base64
        const bytes = new Uint8Array(buffer);
        let binary = '';
        for (let i = 0; i < bytes.length; i++) {
            binary += String.fromCharCode(bytes[i]);
        }
        return btoa(binary);
    }

    // How long a chunk waits for recording to become active before playing
    // anyway. Recording start can fail or stop mid-session; holding audio on
    // it forever meant a permanently silent AI whose watchdogs all looked
    // healthy (the audio *events* still arrived). Better an unrecorded answer
    // than a silent AI.
    private readonly RECORDING_WAIT_FALLBACK_MS = 10000;
    private _recordingFallbackLogged = false;

    private logRecordingFallbackOnce(): void {
        if (this._recordingFallbackLogged) {
            return;
        }
        this._recordingFallbackLogged = true;
        console.warn(
            'AI audio playing without an active recording (fallback after wait).',
        );
        LogRocket.track('ai-audio-recording-fallback');
        Sentry.captureMessage('AI audio playing without active recording', {
            level: 'warning',
            tags: {
                component: 'ai-moderator',
                event_type: 'recording_fallback',
                provider: this._provider,
            },
        });
    }

    feedAudio(data: any, sampleRate: number = 24000) {
        race(
            // Wait until recording is active, then play this chunk exactly once.
            // Using filter (not distinctUntilChanged) means a chunk fed before
            // recording starts — e.g. the AI's first utterance on connect — is
            // held and played once recording begins, instead of being evaluated
            // against the (false) state and dropped. take(1) keeps it to a single
            // emission so no per-chunk subscription lingers for the whole
            // interview (the CPU/memory leak the original take(1) addressed).
            this.recordingService.isRecording.pipe(
                filter((isRecording) => isRecording),
            ),
            // Fallback: if recording still isn't active after the wait window,
            // play regardless (see RECORDING_WAIT_FALLBACK_MS).
            timer(this.RECORDING_WAIT_FALLBACK_MS),
        )
            .pipe(takeUntil(this.unsubscribe$), delay(500), take(1))
            .subscribe(() => {
                if (
                    this.participantService.micEnabled &&
                    !this._hasAiSpokenOnce
                ) {
                    this.participantService.togglemic();
                }

                if (!this.recordingService.isRecording.value) {
                    this.logRecordingFallbackOnce();
                }

                // Assuming you already have audioDataView as Int16Array
                let audioDataView = new Int16Array(data);

                if (audioDataView.length === 0) {
                    console.error('Received audio data is empty.');
                    return;
                }

                // Create an audio buffer
                let audioBuffer = this.audioContextOut.createBuffer(
                    1, // mono channel
                    audioDataView.length, // number of frames
                    sampleRate, // sample rate
                );

                // Get the channel data (the actual audio samples)
                let audioBufferChannel = audioBuffer.getChannelData(0);

                // Convert the Int16Array (PCM data) into the Float32Array for the AudioContext
                for (let i = 0; i < audioDataView.length; i++) {
                    audioBufferChannel[i] = audioDataView[i] / 32768; // Convert linear16 PCM to float [-1, 1]
                }

                // Create a buffer source for the AudioContext
                let source = this.audioContextOut.createBufferSource();
                source.buffer = audioBuffer;

                // Connect the source to the destination node
                source.connect(this.destination);

                // Optionally connect to the default audio output (speakers)
                // source.connect(this.audioContextOut.destination);

                // Check the current time of the audio context and start the audio
                if (this.startTime < this.audioContextOut.currentTime) {
                    this.startTime = this.audioContextOut.currentTime;
                }

                source.start(this.startTime);

                // Update the start time for future audio segments
                this.startTime += audioBuffer.duration;

                // Prune on completion — without this every chunk's source
                // (and its decoded AudioBuffer) was retained for the whole
                // interview unless an interruption happened to clear the
                // array: tens of MB over a long session.
                source.onended = () => {
                    const index = this.scheduledAudioSources.indexOf(source);
                    if (index !== -1) {
                        this.scheduledAudioSources.splice(index, 1);
                    }
                };

                this.scheduledAudioSources.push(source);
            });
    }

    createAudioHandler() {
        return (event: any) => {
            // Always sanity-check WebSocket
            if (!this.isWebSocketOpen()) {
                this.stopAudioProcessing();
                return;
            }

            // Sanity check event data type
            if (!event.data) {
                return;
            }

            if (event.data.type === 'sampleRateDetected') {
                console.log(
                    `AudioProcessor initialized.\nInput sample rate: ${event.data.detectedSampleRate} Hz`,
                );

                this.setSampleRate(event.data.detectedSampleRate);

                return;
            }

            if (event.data.type === 'outputSampleRate') {
                console.log(
                    `AudioProcessor initialized.\nTarget sample rate: ${event.data.outputSampleRate} Hz`,
                );
                return;
            }

            if (
                !(event.data instanceof ArrayBuffer) ||
                event.data.byteLength === 0
            ) {
                return;
            }

            const int16Array = new Int16Array(event.data);
            this.accumulatedSamples.push(int16Array);
            this.accumulatedLength += int16Array.length;

            // Batch to ~100ms blocks before queueing for send.
            if (this.accumulatedLength < this.SEND_THRESHOLD_SAMPLES) {
                return;
            }

            const merged = new Int16Array(this.accumulatedLength);
            let offset = 0;
            for (const chunk of this.accumulatedSamples) {
                merged.set(chunk, offset);
                offset += chunk.length;
            }
            this.accumulatedSamples = [];
            this.accumulatedLength = 0;

            this.audioBufferQueue.push(merged);

            if (!this.isSending) {
                this.processAudioQueue();
            }
        };
    }

    async startStreamingAudioToWebsocket(sendSettings = true) {
        let participants: any = DailyIframe.getCallInstance()?.participants();

        if (!participants?.local?.audioTrack) {
            console.error('No local audio track available');

            // Cancel any retry already in flight so overlapping calls (e.g. a
            // fresh ws.onopen landing mid-retry) can't spawn parallel chains.
            this.clearAudioTrackRetry();

            // Don't re-arm audio if the interview is legitimately ending.
            if (this._endRequested || this.callEnding) {
                return;
            }

            if (
                this.audioTrackRetryAttempts < this.maxAudioTrackRetryAttempts
            ) {
                // Transient race after reconnect: retry shortly.
                this.audioTrackRetryAttempts++;
                this.audioTrackRetryTimer = setTimeout(() => {
                    this.audioTrackRetryTimer = null;
                    this.startStreamingAudioToWebsocket(sendSettings);
                }, this.audioTrackRetryDelayMs);
            } else {
                // Retries exhausted: the mic track is genuinely gone (revoked
                // permission, unplugged device). The WS is healthy, so a WS
                // reconnect won't help — surface the existing actionable
                // "Rejoin" banner. reason:'audio-track' keeps this separable
                // from WS-reconnect exhaustion in LogRocket.
                this.audioTrackRetryAttempts = 0;
                this.connectionState$.next('failed');
                LogRocket.track('ai-connection-failed', {
                    reason: 'audio-track',
                });
            }
            return;
        }

        // Track acquired — clear any pending retry and reset the budget.
        this.clearAudioTrackRetry();
        this.audioTrackRetryAttempts = 0;

        // Close a prior processing context if two opens happened without an
        // intervening close (browsers cap ~6 AudioContexts; a leaked one can
        // make the AI "stop hearing" the participant unrecoverably).
        if (this.audioContext) {
            this.stopAudioProcessing();
        }

        this.audioContext = new AudioContext();
        let audioStream = new MediaStream([participants.local.audioTrack]);
        // Remember which track the graph is bound to, so a later Daily track
        // swap (ICE restart / SFU switch) can be detected and rebound.
        this._boundAudioTrackId = participants.local.audioTrack.id ?? null;

        let audioSource =
            this.audioContext.createMediaStreamSource(audioStream);

        let targetRate = this.audioContext.sampleRate;
        console.log('Sample rate:', this.audioContext.sampleRate);

        if (this.provider === 'elevenlabs') {
            targetRate = 16000; // ElevenLabs expects 16kHz
        }

        try {
            await this.audioContext.audioWorklet.addModule(
                'assets/javascript/audio-processor.js',
            );
        } catch (error) {
            console.error('Failed to load AudioWorklet module:', error);
            return;
        }

        this.workletNode = new AudioWorkletNode(
            this.audioContext,
            'audio-processor',
            {
                processorOptions: {
                    targetSampleRate: targetRate,
                },
            },
        );
        audioSource.connect(this.workletNode);
        this.workletNode.connect(this.audioContext.destination);

        // Use the handler created by createAudioHandler
        this.workletNode.port.onmessage = this.createAudioHandler();
    }

    listenForMicChanges(e: any) {
        // @ts-ignore
        const newMicDeviceId = e?.devices?.mic?.deviceId;
        if (newMicDeviceId) {
            this.workletNode?.disconnect();
            this.workletNode = null;

            try {
                this.audioContext?.close().then(() => {
                    this.audioContext = null;
                    this.startStreamingAudioToWebsocket(false);
                });
            } catch (error) {
                console.log('Failed to close AudioContext:', error);
            }
        }
    }

    // Bug (stale mic track): Daily can replace the local mic track on an ICE
    // restart / network reconnect / SFU switch. The uplink graph binds one
    // specific MediaStreamTrack in startStreamingAudioToWebsocket, so after a
    // swap it keeps reading the dead track and the agent hears silence while the
    // WS/downlink stay healthy (no existing watchdog covers this). Rebuild the
    // graph against the current track when it actually changes. Wired from the
    // Daily track-started/stopped handler for the local audio track.
    onLocalAudioTrackChanged(): void {
        if (this._endRequested || this.callEnding) {
            return;
        }
        if (this.provider !== 'elevenlabs' || !this.isWebSocketOpen()) {
            return;
        }
        // Daily types local.audioTrack as `MediaStreamTrack | false`, so guard
        // truthiness (optional chaining alone doesn't exclude `false`).
        const currentTrack =
            DailyIframe.getCallInstance()?.participants()?.local?.audioTrack;
        const currentId = currentTrack ? currentTrack.id : null;
        // No current track yet (mid-swap), or unchanged: nothing to rebind.
        // startStreamingAudioToWebsocket's own retry handles (re)acquisition.
        if (!currentId || currentId === this._boundAudioTrackId) {
            return;
        }
        console.warn(
            'Local mic track changed; rebinding AI uplink audio graph to the current track.',
        );
        LogRocket.track('ai-uplink-rebind', { reason: 'track-changed' });
        this.startStreamingAudioToWebsocket(false);
    }

    async processAudioQueue() {
        if (!this.ws || !this.isWebSocketOpen()) {
            console.warn('WebSocket is not open. Stopping queue processing.');
            this.audioBufferQueue = [];
            this.isSending = false;
            return;
        }

        this.isSending = true;

        while (this.audioBufferQueue.length > 0) {
            if (this.ws.bufferedAmount > 65536) {
                // Edge-triggered: warn once when we enter the saturated state,
                // not on every ~50ms retry, to avoid flooding the console.
                if (!this.bufferFullWarned) {
                    console.warn(
                        `WebSocket buffer full (${this.ws.bufferedAmount}B), throttling sends...`,
                    );
                    this.bufferFullWarned = true;
                }
                await this.wait(50);
                continue;
            }

            if (this.bufferFullWarned) {
                console.warn('WebSocket buffer drained, resuming normal sends.');
                this.bufferFullWarned = false;
            }

            let audioData = this.audioBufferQueue.shift();
            if (audioData) {
                if (this.provider === 'elevenlabs') {
                    let msg = {
                        user_audio_chunk: this.int16ArrayToBase64(audioData),
                    };
                    this.ws?.send(JSON.stringify(msg));
                }

                // NOTE: audio chunks are intentionally NOT buffered here. Pushing
                // every chunk into recordedAudio grew the array unbounded (~32KB/s,
                // ~30MB over a 16-min interview) and it was never read anywhere —
                // a pure memory leak. If raw audio capture is ever needed, use a
                // bounded/streamed strategy rather than an in-memory array.
            }
        }

        this.isSending = false;
    }

    private wait(ms: number) {
        return new Promise((resolve) => setTimeout(resolve, ms));
    }

    clearScheduledAudio() {
        this.scheduledAudioSources.forEach((source: any) => {
            source.stop();
        });
        this.scheduledAudioSources = [];

        this.startTime = -1;
    }

    formatDiscussion(data: any): string {
        const guide = this.parseDiscussionData(data);
        return this.buildFormattedDiscussion(guide);
    }

    parseDiscussionData(data: any[]): DiscussionGuide {
        // A non-array guide (malformed API data) would make data.find/.filter
        // throw; return an empty guide so the AI still gets a (blank) prompt
        // rather than setup aborting entirely.
        if (!Array.isArray(data)) {
            console.warn('Discussion guide is not an array');
            return {
                introduction: { questions: [] },
                mainSections: [],
                conclusion: { questions: [] },
            };
        }

        if (Array.isArray(data[0])) {
            return {
                introduction: { questions: data[0] },
                mainSections: data.slice(1, -1) as Section[],
                conclusion: { questions: data[data.length - 1] },
            };
        }

        const intro = data.find((item) => item.title === 'Introduction');
        const conclusion = data.find((item) => item.title === 'Conclusion');

        if (!intro || !conclusion) {
            console.warn(
                'Missing introduction or conclusion in discussion guide',
            );
        }

        return {
            introduction: intro
                ? { questions: intro.questions || [], UID: intro.UID }
                : { questions: [] },
            mainSections: data.filter(
                (item) =>
                    item.title !== 'Introduction' &&
                    item.title !== 'Conclusion',
            ) as Section[],

            conclusion: conclusion
                ? { questions: conclusion.questions || [], UID: conclusion.UID }
                : { questions: [] },
        };
    }

    buildFormattedDiscussion(guide: DiscussionGuide): string {
        const parts: string[] = [];

        if (guide.introduction.UID) {
            parts.push(`### Introduction [UID: ${guide.introduction.UID}]`);
        } else {
            parts.push('### Introduction');
        }
        guide.introduction.questions.forEach((item) => {
            parts.push(`- ${item}`);
        });

        parts.push('\n### Main Discussion\n');
        guide.mainSections.forEach((section) => {
            const title = section.title || section.key || 'Discussion Topic';

            if (section.UID) {
                parts.push(`#### ${title} [UID: ${section.UID}]`);
            } else {
                parts.push(`#### ${title}`);
            }

            (section.questions || []).forEach((question, index) => {
                parts.push(`- ${question}`);

                const probing = section.probingNotes?.[index];
                if (probing) {
                    // Handle new format with selectedType
                    if ('selectedType' in probing) {
                        switch (probing.selectedType) {
                            case 'smart_probing':
                                // Process smart probing rules
                                probing.rules.forEach((rule) => {
                                    if (
                                        rule &&
                                        rule.followUpQuestion &&
                                        rule.followUpQuestion.trim() !== ''
                                    ) {
                                        const triggerInfo = rule.triggerWords
                                            ? `Listen for: "${rule.triggerWords}" - `
                                            : ' ';

                                        if (triggerInfo.trim() !== '') {
                                            parts.push(
                                                `  - *Probe ${triggerInfo}ONLY if triggered:* ${rule.followUpQuestion}`,
                                            );
                                        }
                                    }
                                });
                                break;

                            case 'general_probing_level':
                                // Process general probing level
                                const level =
                                    probing.generalProbingLevel.selectedLevel;
                                const explanation =
                                    probing.generalProbingLevel.explanations[
                                        level.toString()
                                    ];

                                let levelInfo = '';

                                switch (level) {
                                    case 0:
                                        levelInfo = 'None';
                                        break;
                                    case 1:
                                        levelInfo = 'Normal';
                                        break;
                                    case 2:
                                        levelInfo = 'Deep-dive';
                                        break;
                                    default:
                                        levelInfo = 'Normal';
                                        break;
                                }

                                if (explanation) {
                                    parts.push(
                                        `  - *General Probe:* ${levelInfo}`,
                                    );
                                }

                                if (
                                    section.suggested_probes &&
                                    Array.isArray(
                                        section.suggested_probes[index],
                                    ) &&
                                    section.suggested_probes[index].length > 0
                                ) {
                                    let probeCount = 0;
                                    if (level === 1) {
                                        probeCount = 2;
                                    } else if (level === 2) {
                                        probeCount = 4;
                                    }

                                    if (probeCount > 0) {
                                        const probesToAdd =
                                            section.suggested_probes[
                                                index
                                            ].slice(0, probeCount);
                                        parts.push(
                                            `  - *Suggested Probes:* ${probesToAdd.join(' | ')}`,
                                        );
                                    }
                                }
                                break;

                            case 'ai_context':
                                // Process AI context
                                if (
                                    probing.aiContext &&
                                    probing.aiContext.trim() !== ''
                                ) {
                                    parts.push(
                                        `  - *AI Context:* ${probing.aiContext}`,
                                    );
                                }
                                break;

                            default:
                                // Handle unknown probing types gracefully
                                console.warn(
                                    `Unknown probing type: ${(probing as any).selectedType}`,
                                );
                                break;
                        }
                    }
                    // Handle old format (backward compatibility)
                    else if (
                        'rules' in probing &&
                        Array.isArray(probing.rules)
                    ) {
                        probing.rules.forEach((rule) => {
                            if (
                                rule &&
                                rule.followUpQuestion &&
                                rule.followUpQuestion.trim() !== ''
                            ) {
                                const triggerInfo = rule.triggerWords
                                    ? `Listen for: "${rule.triggerWords}" - `
                                    : ' ';
                                const levelInfo = rule.probingLevel
                                    ? `(Level ${rule.probingLevel}) `
                                    : ' ';
                                if (triggerInfo.trim() !== '') {
                                    parts.push(
                                        `  - *Probe ${levelInfo}${triggerInfo}ONLY if triggered:* ${rule.followUpQuestion}`,
                                    );
                                }
                            }
                        });
                    }
                }
            });

            parts.push('');
        });

        if (guide.conclusion.UID) {
            parts.push(`### Conclusion [UID: ${guide.conclusion.UID}]`);
        } else {
            parts.push('### Conclusion');
        }
        guide.conclusion.questions.forEach((item) => {
            parts.push(`- ${item}`);
        });

        return parts.join('\n');
    }

    closeWebSocket(
        retry: boolean,
        code: number = this.WS_CLOSE.CLIENT_RECOVERY,
        reason: string = 'client-close',
    ) {
        console.log('Closing WebSocket connection...');
        // Stop the stalled-turn watchdog so it can't fire during teardown or
        // while a reconnect is in flight. The greeting watchdog is re-armed by
        // ws.onopen if the AI still hasn't spoken.
        this.clearTurnWatchdog();
        this.clearGreetingWatchdog();
        this.clearLivenessWatchdog();
        if (this.ws) {
            this.ws.onopen = null;
            this.ws.onmessage = null;
            this.ws.onerror = null;
            this.ws.onclose = null;
            this.ws.close(code, reason);
            this.ws = undefined;
        }

        // Stop audio processing
        this.clearAudioTrackRetry();
        this.stopAudioProcessing();

        // Stop any AI speech still scheduled on the playback context and reset
        // the scheduling cursor. Otherwise queued pre-disconnect audio keeps
        // playing through the reconnect and the new session's greeting is
        // scheduled after the stale tail — the participant hears the old
        // sentence finish, then "Sorry, we got disconnected…" (perceived as
        // the AI repeating itself). On the end-call path this is a no-op:
        // endCall() only closes the socket after waitForAllAudioToComplete().
        this.clearScheduledAudio();

        // Cancel any pending reconnect backoff. On the end/leave path (retry
        // false) this stops a straggler timer from reopening the socket after
        // teardown; on the retry path handleWebSocketReconnect schedules a fresh
        // one below.
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }

        if (retry) {
            console.log('Retrying WebSocket connection...');
            this.handleWebSocketReconnect();
        }
    }

    isWebSocketOpen() {
        return this.ws?.readyState === WebSocket.OPEN;
    }

    private handleTopicTracking(topicUID: string): void {
        // Get topic details from discussion guide using UID
        const topicData = this.getTopicByUID(topicUID);
        if (!topicData) {
            console.warn('Topic not found for UID:', topicUID);
            return;
        }

        const topicTitle =
            topicData.title || topicData.key || 'Discussion Topic';

        // Check if topic already started (avoid duplicates)
        const existingTopic = this.completedTopicsTimestamps.find(
            (t) => t.topicUID === topicUID,
        );

        if (!existingTopic) {
            // Add to started topics
            this.completedTopicsTimestamps.push({
                topicUID: topicUID,
                topicTitle: topicTitle,
                startTime: new Date().getTime(),
                questionsAnswered: 0,
            });

            // Started-topic set changed — persist for resume-after-reload.
            this.persistResumeState();

            // Check for stimulus and emit event if one exists
            const stimulusData = this.getStimulusForTopic(topicUID);
            if (stimulusData) {
                this.stimulusDisplaySubject.next({
                    stimuli_url: stimulusData.stimuli_url,
                    stimulusImages: stimulusData.stimulusImages,
                    stimulusMedia: stimulusData.stimulusMedia,
                    topicTitle: topicTitle,
                });
                this.startStimulusActivity();
            }

            // Start visual analysis if this topic opts in
            if (
                this.visualAnalysisService.isEnabled() &&
                (topicData.addVision || topicData.visualAnalysis?.enabled)
            ) {
                this.visualAnalysisService.startTopicAnalysis(
                    topicUID,
                    topicTitle,
                    topicData.questions || [],
                );
                // Anchor the agent's perception identity at the moment vision
                // activates, before the first analysed frame can arrive.
                this.sendContextualUpdate(
                    `[You see — webcam] You can see the participant clearly on their webcam.`,
                );
            }
        }
    }



    private randomizeDiscussionGuide(discussionGuideData: any): any {
        if (typeof discussionGuideData === 'string') {
            try {
                discussionGuideData = JSON.parse(discussionGuideData);
            } catch (error) {
                return discussionGuideData;
            }
        }

        // Handle both array and object formats
        let sectionsToRandomize: any[];
        let isArrayFormat = false;

        if (
            Array.isArray(discussionGuideData) &&
            Array.isArray(discussionGuideData[0])
        ) {
            // Array format: [intro, section1, section2, ..., conclusion]
            sectionsToRandomize = discussionGuideData.slice(1, -1);
            isArrayFormat = true;
        } else if (Array.isArray(discussionGuideData)) {
            // Object format: [intro, section1, section2, conclusion]
            sectionsToRandomize = discussionGuideData.filter(
                (item) =>
                    item.title !== 'Introduction' &&
                    item.title !== 'Conclusion',
            );
        } else {
            // Invalid format, return as-is
            return discussionGuideData;
        }

        // Separate topics with and without shufflable flag
        const shufflableTopics: any[] = [];
        const nonShufflableTopics: any[] = [];

        sectionsToRandomize.forEach((section) => {
            const isShufflable = section.stimulusShuffleable === true;

            if (isShufflable) {
                shufflableTopics.push(section);
            } else {
                nonShufflableTopics.push(section);
            }
        });

        // Only randomize if there are 2 or more shufflable topics
        if (shufflableTopics.length >= 2) {
            // Fisher-Yates shuffle algorithm
            for (let i = shufflableTopics.length - 1; i > 0; i--) {
                const j = Math.floor(Math.random() * (i + 1));
                [shufflableTopics[i], shufflableTopics[j]] = [
                    shufflableTopics[j],
                    shufflableTopics[i],
                ];
            }
        }

        // Merge back maintaining relative positions of non-shufflable topics
        const randomizedSections: any[] = [];
        let shufflableIndex = 0;
        let nonShufflableIndex = 0;

        sectionsToRandomize.forEach((originalSection) => {
            const isShufflable = originalSection.stimulusShuffleable === true;

            if (isShufflable) {
                randomizedSections.push(shufflableTopics[shufflableIndex]);
                shufflableIndex++;
            } else {
                randomizedSections.push(
                    nonShufflableTopics[nonShufflableIndex],
                );
                nonShufflableIndex++;
            }
        });

        // Reconstruct the discussion guide with randomized sections
        if (isArrayFormat) {
            const result = [...discussionGuideData];
            for (let i = 0; i < randomizedSections.length; i++) {
                result[i + 1] = randomizedSections[i];
            }
            return result;
        } else {
            // Object format - reconstruct maintaining intro and conclusion
            const intro = discussionGuideData.find(
                (item: any) => item.title === 'Introduction',
            );
            const conclusion = discussionGuideData.find(
                (item: any) => item.title === 'Conclusion',
            );

            const result = [];
            if (intro) result.push(intro);
            result.push(...randomizedSections);
            if (conclusion) result.push(conclusion);

            return result;
        }
    }

    private sendToolResponse(toolCallId: string, result: any, isError: boolean = false) {
        if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
            console.error('Cannot send tool response - WebSocket not open', {
                wsExists: !!this.ws,
                readyState: this.ws?.readyState
            });
            return;
        }

        const response = {
            type: 'client_tool_result',
            tool_call_id: toolCallId,
            result: JSON.stringify(result),
            is_error: isError,
        };

        this.ws.send(JSON.stringify(response));
    }


    buildReconnectFirstMessage(): string {
        const completedTitles = this.completedTopics.map((t) => t.topicTitle);

        const lastCompletedUID =
            this.completedTopics.length > 0
                ? this.completedTopics[this.completedTopics.length - 1]
                      .topicUID
                : null;

        let nextTopicName = '';
        if (lastCompletedUID) {
            const nextTopic = this.getNextTopic(lastCompletedUID);
            if (nextTopic) {
                nextTopicName =
                    nextTopic.title || nextTopic.key || 'Discussion Topic';
            }
        }

        if (completedTitles.length > 0 && nextTopicName) {
            return (
                `Sorry it looks like we got disconnected. We've already covered ${completedTitles.join(', ')}. ` +
                `Let's continue with ${nextTopicName}.`
            );
        }

        if (completedTitles.length > 0) {
            return (
                `Sorry it looks like we got disconnected. We've already covered ${completedTitles.join(', ')}. ` +
                `Would you like me to repeat the last question?`
            );
        }

        return 'Sorry it looks like we got disconnected, would you like me to repeat the last question?';
    }

    /**
     * Builds the dynamic variables that let the ElevenLabs workflow's resume_router
     * jump straight to the in-progress topic on reconnect, instead of restarting at
     * the introduction. Returns null when there is nothing to resume into.
     *
     * - resume_topic_index: the participant's sequence position among real topics
     *   (intro/conclusion excluded) = completed real topics + 1. This maps to the
     *   generic workflow node node_01topic{index}_start (content is fed per-topic, so
     *   node position tracks sequence count regardless of per-session randomisation).
     *   The conclusion is addressed by the 'conclusion' sentinel.
     * - next_topic_*: the in-progress topic's content, mirroring what the
     *   markTopicCompleted2 tool response normally supplies.
     */
    private buildResumeRoutingVariables(): {
        resume_topic_index: string;
        next_topic_title: string;
        next_topic_content: string;
        next_topic_uid: string;
        // Scalar only: conversation_initiation_client_data.dynamic_variables
        // rejects objects (unlike the markTopicCompleted2 tool result, which can
        // carry the full {has_stimulus,type} object). Mirrors the boolean the agent
        // placeholder declares for next_topic_stimulus.
        next_topic_stimulus: boolean;
    } | null {
        if (this.completedTopics.length === 0 || !this.rawDiscussionGuide) {
            return null;
        }

        let guide: DiscussionGuide;
        try {
            guide = this.parseDiscussionData(
                JSON.parse(this.rawDiscussionGuide),
            );
        } catch {
            return null;
        }

        const introUID = guide.introduction?.UID;
        const conclusionUID = guide.conclusion?.UID;

        const lastCompletedUID =
            this.completedTopics[this.completedTopics.length - 1].topicUID;
        const resumeTopic = this.getNextTopic(lastCompletedUID);
        if (!resumeTopic) {
            return null;
        }

        // Sequence position = number of completed real topics (intro/conclusion
        // excluded) + 1; the conclusion routes via a dedicated sentinel.
        const realCompletedCount = this.completedTopics.filter(
            (t) => t.topicUID !== introUID && t.topicUID !== conclusionUID,
        ).length;

        const resumeIndex =
            resumeTopic.UID && resumeTopic.UID === conclusionUID
                ? 'conclusion'
                : String(realCompletedCount + 1);

        return {
            resume_topic_index: resumeIndex,
            next_topic_title:
                resumeTopic.title || resumeTopic.key || 'Discussion Topic',
            next_topic_content: this.formatTopicContent(resumeTopic),
            next_topic_uid: resumeTopic.UID,
            next_topic_stimulus: resumeTopic.UID
                ? this.getStimulusInfo(resumeTopic.UID).has_stimulus
                : false,
        };
    }

    private getNextTopic(currentUID: string): any | null {
        if (!this.rawDiscussionGuide) return null;
        try {
            const guide = this.parseDiscussionData(
                JSON.parse(this.rawDiscussionGuide),
            );

            // Check Introduction
            if (guide.introduction.UID === currentUID) {
                return guide.mainSections.length > 0
                    ? guide.mainSections[0]
                    : { ...guide.conclusion, title: 'Conclusion' };
            }

            // Check Main Sections
            const index = guide.mainSections.findIndex(
                (s) => s.UID === currentUID,
            );
            if (index !== -1) {
                if (index < guide.mainSections.length - 1) {
                    return guide.mainSections[index + 1];
                } else {
                    return { ...guide.conclusion, title: 'Conclusion' };
                }
            }

            // Check Conclusion (nothing after)
            if (guide.conclusion.UID === currentUID) {
                return null;
            }

            return null;
        } catch (e) {
            console.error('Error determining next topic:', e);
            return null;
        }
    }

    private formatTopicContent(section: any): string {
        const parts: string[] = [];
        if (section.questions && Array.isArray(section.questions)) {
            section.questions.forEach((question: string, index: number) => {
                parts.push(`${index + 1}. ${question}`);

                if (
                    section.probingNotes &&
                    section.probingNotes[index]
                ) {
                    const probing = section.probingNotes[index];
                    if (probing) {
                        // Handle ai_context
                        if (
                            'selectedType' in probing &&
                            probing.selectedType === 'ai_context' &&
                            probing.aiContext
                        ) {
                            parts.push(
                                `   ↳ PROBING INSTRUCTIONS: CONTEXT: ${probing.aiContext}`,
                            );
                        }
                        // Handle general_probing_level
                        else if (
                            'selectedType' in probing &&
                            probing.selectedType === 'general_probing_level' &&
                            probing.generalProbingLevel
                        ) {
                            const level =
                                probing.generalProbingLevel.selectedLevel;
                            const levelText =
                                level === 1
                                    ? 'Normal'
                                    : level === 2
                                    ? 'Deep-dive'
                                    : 'None';
                            parts.push(
                                `   ↳ PROBING INSTRUCTIONS: General Probe Level: ${levelText}`,
                            );
                        }
                        // Handle smart_probing
                        else if (
                            'selectedType' in probing &&
                            probing.selectedType === 'smart_probing' &&
                            probing.rules
                        ) {
                             probing.rules.forEach((rule: any) => {
                                if (rule && rule.followUpQuestion) {
                                     parts.push(
                                        `   ↳ PROBING INSTRUCTIONS: If "${rule.triggerWords}" -> Ask: ${rule.followUpQuestion}`,
                                    );
                                }
                             });
                        }
                    }
                }
            });
        }
        return parts.join('\n');
    }

    // Fast path for 'audio' frames: several arrive per second while the AI
    // speaks, so they are handled without the Sentry span/breadcrumb/tag work
    // the low-frequency events get — that per-frame instrumentation cost real
    // main-thread CPU over a long interview.
    private handleAudioEvent(msgObj: any): void {
        try {
            // AI is producing audio; the turn is not stalled.
            this.clearTurnWatchdog();
            const eventId = msgObj['audio_event']['event_id'];

            if (eventId < this._lastInterruptEventId) {
                console.warn(
                    'Discarding stale audio due to prior interruption',
                );
                return;
            }
            const base64Audio = this.base64ToInt16Array(
                msgObj['audio_event']['audio_base_64'],
            );
            this.feedAudio(base64Audio, 16000);
            this.handleFirstAiAudioComplete();
        } catch (error) {
            Sentry.captureException(error, {
                tags: {
                    component: 'ai-moderator',
                    event_type: 'audio',
                    provider: 'elevenlabs',
                },
                extra: {
                    eventId: msgObj['audio_event']?.['event_id'],
                    audioDataLength:
                        msgObj['audio_event']?.['audio_base_64']?.length,
                },
            });
        }
    }

    // Fast path for the ~2s ping heartbeat — see handleAudioEvent.
    private handlePingEvent(msgObj: any): void {
        try {
            console.log('Received ping from ElevenLabs');
            // A ping is the connection's heartbeat (~every 2s) and the sole
            // proof of liveness — reset the silent-drop watchdog. No other
            // frame counts, so a wedged agent still emitting audio/vad frames
            // without pings is correctly detected as dead.
            this.resetLivenessWatchdog();

            const pingEventId = msgObj['ping_event']?.['event_id'];

            this.ws?.send(
                JSON.stringify({
                    type: 'pong',
                    event_id: pingEventId,
                }),
            );
        } catch (error) {
            Sentry.captureException(error, {
                tags: {
                    component: 'ai-moderator',
                    event_type: 'ping_event',
                    provider: 'elevenlabs',
                },
            });
        }
    }

    triggerElevenLabsEvent(event: any) {
        let parsed: any;
        try {
            parsed = JSON.parse(event.data);
        } catch (parseError) {
            Sentry.captureException(parseError, {
                tags: {
                    component: 'ai-moderator',
                    event_type: 'parse_error',
                    provider: 'elevenlabs',
                },
                extra: {
                    rawEventData: event.data,
                    conversationId: this._conversationId,
                    userId: this._userId,
                },
            });
            console.error('Failed to parse ElevenLabs event:', parseError);
            return;
        }

        // JSON.parse('null')/'42'/an array yields a non-object; guard before
        // reading parsed['type'] so a malformed frame can't throw in onmessage.
        if (!parsed || typeof parsed !== 'object') {
            return;
        }

        // High-frequency frames are handled before (and instead of) the
        // Sentry-instrumented path below.
        switch (parsed['type']) {
            case 'audio':
                this.handleAudioEvent(parsed);
                return;
            case 'ping_event':
            case 'ping':
                this.handlePingEvent(parsed);
                return;
            // Benign, high-frequency telemetry — explicitly ignored so it
            // does not flood Sentry/the hot path as an "unknown" event.
            case 'vad_score':
                return;
        }

        Sentry.startSpan(
            {
                name: 'triggerElevenLabsEvent',
                op: 'elevenlabs.event.process',
                attributes: {
                    component: 'ai-moderator',
                    provider: 'elevenlabs',
                },
            },
            () => {
                const eventStartTime = Date.now();
                let hasError = false;
                const msgObj: any = parsed;
                msgObj.startTime = eventStartTime;

                // Track function entry and add context
                Sentry.addBreadcrumb({
                    message: 'ElevenLabs WebSocket event received',
                    category: 'elevenlabs',
                    level: 'info',
                    data: {
                        eventSize: event.data?.length || 0,
                        timestamp: eventStartTime,
                    },
                });

                // Update Sentry tags for this event
                Sentry.setTag('event_type', msgObj['type']);
                Sentry.setTag('conversation_id', this._conversationId);
                Sentry.setTag('user_id', this._userId);

                // Set Sentry context for this event
                Sentry.setContext('elevenlabs_event', {
                    type: msgObj['type'],
                    conversationId: this._conversationId,
                    userId: this._userId,
                    provider: this._provider,
                    hasAiSpoken: this._hasAiSpokenOnce,
                });

                // Track event type metrics
                Sentry.addBreadcrumb({
                    message: `Processing ElevenLabs event: ${msgObj['type']}`,
                    category: 'elevenlabs.event',
                    level: 'info',
                    data: {
                        eventType: msgObj['type'],
                        conversationId: this._conversationId,
                    },
                });

                switch (msgObj['type']) {
                    case 'conversation_initiation_metadata':
                        try {
                            if (
                                msgObj[
                                    'conversation_initiation_metadata_event'
                                ] &&
                                msgObj[
                                    'conversation_initiation_metadata_event'
                                ]['conversation_id']
                            ) {
                                this._conversationId =
                                    msgObj[
                                        'conversation_initiation_metadata_event'
                                    ]['conversation_id'];

                                Sentry.setTag(
                                    'conversation_id',
                                    this._conversationId,
                                );
                                Sentry.addBreadcrumb({
                                    message: 'Conversation initiated',
                                    category: 'elevenlabs.conversation',
                                    level: 'info',
                                    data: {
                                        conversationId: this._conversationId,
                                    },
                                });

                                // Persist now that we have a conversation id —
                                // this is the point from which a page reload
                                // can meaningfully resume.
                                this.persistResumeState();
                            }

                        } catch (error) {
                            hasError = true;
                            Sentry.captureException(error, {
                                tags: {
                                    component: 'ai-moderator',
                                    event_type:
                                        'conversation_initiation_metadata',
                                    provider: 'elevenlabs',
                                },
                            });
                        }
                        break;
                    case 'interruption':
                        try {
                            const interruptEventId =
                                msgObj['interruption_event']['event_id'];

                            Sentry.addBreadcrumb({
                                message: 'Interruption event received',
                                category: 'elevenlabs.interruption',
                                level: 'info',
                                data: {
                                    eventId: interruptEventId,
                                    previousInterruptEventId:
                                        this._lastInterruptEventId,
                                    scheduledAudioSources:
                                        this.scheduledAudioSources.length,
                                },
                            });

                            this._lastInterruptEventId = interruptEventId;
                            this.clearScheduledAudio();

                            Sentry.addBreadcrumb({
                                message: 'Audio cleared due to interruption',
                                category: 'elevenlabs.interruption',
                                level: 'info',
                                data: {
                                    eventId: interruptEventId,
                                },
                            });
                        } catch (error) {
                            hasError = true;
                            Sentry.captureException(error, {
                                tags: {
                                    component: 'ai-moderator',
                                    event_type: 'interruption',
                                    provider: 'elevenlabs',
                                },
                            });
                        }
                        break;
                    case 'agent_response':
                        try {
                            let payload = { content: '', role: 'assistant' };
                            if (
                                msgObj['agent_response_event'] &&
                                msgObj['agent_response_event']['agent_response']
                            ) {
                                payload.content =
                                    msgObj['agent_response_event'][
                                        'agent_response'
                                    ];

                                if (
                                    this.detectVisualStall(payload.content)
                                ) {
                                    this.setAwaitingPostStallVisual();
                                }

                                Sentry.addBreadcrumb({
                                    message: 'Agent response received',
                                    category: 'elevenlabs.agent_response',
                                    level: 'info',
                                    data: {
                                        responseLength: payload.content.length,
                                        conversationLength:
                                            this.conversation.length,
                                        hasContent: !!payload.content.trim(),
                                    },
                                });

                                // Emit AI caption event for ElevenLabs
                                if (
                                    this.provider === 'elevenlabs' &&
                                    payload.content.trim()
                                ) {
                                    this.aiCaptionsSubject.next({
                                        text: payload.content,
                                        user_name: 'AI Moderator',
                                    });

                                    Sentry.addBreadcrumb({
                                        message: 'AI captions updated',
                                        category: 'elevenlabs.captions',
                                        level: 'info',
                                        data: {
                                            captionLength:
                                                payload.content.length,
                                        },
                                    });
                                }
                            }
                            this.updateConversation(payload);
                            // AI produced a turn; the turn is no longer stalled.
                            this.clearTurnWatchdog();
                        } catch (error) {
                            hasError = true;
                            Sentry.captureException(error, {
                                tags: {
                                    component: 'ai-moderator',
                                    event_type: 'agent_response',
                                    provider: 'elevenlabs',
                                },
                                extra: {
                                    responseContent:
                                        msgObj['agent_response_event']?.[
                                            'agent_response'
                                        ],
                                },
                            });
                        }
                        break;
                    case 'user_transcript':
                        try {
                            let userPayload = { content: '', role: 'user' };
                            if (
                                msgObj['user_transcription_event'] &&
                                msgObj['user_transcription_event'][
                                    'user_transcript'
                                ]
                            ) {
                                userPayload.content =
                                    msgObj['user_transcription_event'][
                                        'user_transcript'
                                    ];
                            }

                            Sentry.addBreadcrumb({
                                message: 'User transcript received',
                                category: 'elevenlabs.user_transcript',
                                level: 'info',
                                data: {
                                    transcriptLength:
                                        userPayload.content.length,
                                    conversationLength:
                                        this.conversation.length,
                                    hasContent: !!userPayload.content.trim(),
                                    userId: this._userId,
                                },
                            });

                            this.updateConversation(userPayload);
                            this.visualAnalysisService.flushBatch();
                            this.clearAwaitingPostStallVisual();
                            this.clearStimulusActivity();

                            // Participant's turn is complete; the AI is now
                            // expected to respond. Arm the stalled-turn watchdog.
                            this.startTurnWatchdog();

                            Sentry.addBreadcrumb({
                                message:
                                    'Stimulus activity cleared after user input',
                                category: 'elevenlabs.stimulus',
                                level: 'debug',
                            });
                        } catch (error) {
                            hasError = true;
                            Sentry.captureException(error, {
                                tags: {
                                    component: 'ai-moderator',
                                    event_type: 'user_transcript',
                                    provider: 'elevenlabs',
                                },
                                extra: {
                                    transcript:
                                        msgObj['user_transcription_event']?.[
                                            'user_transcript'
                                        ],
                                },
                            });
                        }
                        break;
                    case 'agent_tool_response':
                        try {
                            const toolName =
                                msgObj['agent_tool_response']?.['tool_name'];

                            Sentry.addBreadcrumb({
                                message: 'Agent tool response received',
                                category: 'elevenlabs.agent_tool_response',
                                level: 'info',
                                data: {
                                    toolName: toolName,
                                },
                            });

                            if (
                                msgObj['agent_tool_response'] &&
                                toolName &&
                                toolName === 'end_call'
                            ) {
                                Sentry.addBreadcrumb({
                                    message: 'End call tool response received',
                                    category: 'elevenlabs.end_call',
                                    level: 'info',
                                    data: {
                                        waitingForAudioCompletion:
                                            this.waitingForAudioCompletion,
                                        isAiSpeaking: this.isAiSpeaking.value,
                                    },
                                });

                                console.log(
                                    'Received end call - waiting for final audio to complete',
                                );
                                // Mark the interview as intentionally ending so a
                                // server-sent close (incl. a clean 1000) isn't
                                // treated as an unexpected drop and reconnected.
                                this._endRequested = true;
                                // Give a brief moment for any final audio to be queued
                                setTimeout(() => {
                                    this.waitForAllAudioToComplete();
                                }, 200);
                            }
                        } catch (error) {
                            hasError = true;
                            Sentry.captureException(error, {
                                tags: {
                                    component: 'ai-moderator',
                                    event_type: 'agent_tool_response',
                                    provider: 'elevenlabs',
                                },
                                extra: {
                                    toolName:
                                        msgObj['agent_tool_response']?.[
                                            'tool_name'
                                        ],
                                },
                            });
                        }
                        break;
                    case 'client_tool_call':
                        try {
                            const toolName =
                                msgObj['client_tool_call']?.['tool_name'];
                            const parameters =
                                msgObj['client_tool_call']?.['parameters'];

                            Sentry.addBreadcrumb({
                                message: 'Client tool call received',
                                category: 'elevenlabs.client_tool_call',
                                level: 'info',
                                data: {
                                    toolName: toolName,
                                    hasParameters: !!parameters,
                                    toolCallId: msgObj['client_tool_call']?.['tool_call_id']
                                },
                            });

                            const toolCallId = msgObj['client_tool_call']?.['tool_call_id'];

                            if (toolName === 'markTopicStarted') {
                                // Legacy tool - just handle the call, no response needed
                                Sentry.addBreadcrumb({
                                    message: 'Topic started tool call',
                                    category: 'elevenlabs.topic',
                                    level: 'info',
                                    data: {
                                        topicUID: parameters?.['UID'],
                                        topicTitle: parameters?.['topicTitle'],
                                        completedTopicsCount:
                                            this.completedTopicsTimestamps
                                                .length,
                                    },
                                });

                                LogRocket.track('markTopicStarted', {
                                    parameters:
                                        msgObj['client_tool_call'][
                                            'parameters'
                                        ],
                                });
                                if (
                                    msgObj['client_tool_call']['parameters'] &&
                                    msgObj['client_tool_call']['parameters'][
                                        'UID'
                                    ]
                                ) {
                                    const topicUID =
                                        msgObj['client_tool_call'][
                                            'parameters'
                                        ]['UID'];

                                    this.handleTopicTracking(topicUID);
                                } else if (
                                    msgObj['client_tool_call']['parameters'] &&
                                    msgObj['client_tool_call']['parameters'][
                                        'topicTitle'
                                    ]
                                ) {
                                    const topicTitle =
                                        msgObj['client_tool_call']['parameters']
                                            .topicTitle;
                                    const topicUID =
                                        this.getTopicUIDByTitle(topicTitle);

                                    if (topicUID) {
                                        this.completedTopicsTimestamps.push({
                                            topicUID: topicUID,
                                            topicTitle: topicTitle,
                                            startTime: new Date().getTime(),
                                            questionsAnswered: 0,
                                        });

                                        // Check for stimulus and emit event if one exists
                                        const stimulusData =
                                            this.getStimulusForTopic(topicUID);
                                        if (stimulusData) {
                                            this.stimulusDisplaySubject.next({
                                                stimuli_url:
                                                    stimulusData.stimuli_url,
                                                stimulusImages:
                                                    stimulusData.stimulusImages,
                                                stimulusMedia:
                                                    stimulusData.stimulusMedia,
                                                topicTitle: topicTitle,
                                            });
                                            this.startStimulusActivity();
                                            // const { url, description, isMultiple } =
                                            //     this.generateStimulusDescription(
                                            //         stimulusData,
                                            //     );
                                            // this.sendStimulusContextUpdate(
                                            //     url,
                                            //     topicTitle,
                                            //     description,
                                            //     isMultiple,
                                            // );
                                        }
                                    }
                                }
                            }
                            if (toolName === 'markTopicStarted2') {
                                Sentry.addBreadcrumb({
                                    message: 'Topic started tool call (v2)',
                                    category: 'elevenlabs.topic',
                                    level: 'info',
                                    data: {
                                        topicUID: parameters?.['UID'],
                                        topicTitle: parameters?.['topicTitle'],
                                        completedTopicsCount:
                                            this.completedTopicsTimestamps
                                                .length,
                                    },
                                });

                                LogRocket.track('markTopicStarted2', {
                                    parameters:
                                        msgObj['client_tool_call'][
                                            'parameters'
                                        ],
                                });

                                let topicUID: string | null = null;

                                if (
                                    msgObj['client_tool_call']['parameters'] &&
                                    msgObj['client_tool_call']['parameters'][
                                        'UID'
                                    ]
                                ) {
                                    topicUID =
                                        msgObj['client_tool_call'][
                                            'parameters'
                                        ]['UID'];

                                    this.handleTopicTracking(topicUID as string);
                                } else if (
                                    msgObj['client_tool_call']['parameters'] &&
                                    msgObj['client_tool_call']['parameters'][
                                        'topicTitle'
                                    ]
                                ) {
                                    const topicTitle =
                                        msgObj['client_tool_call']['parameters']
                                            .topicTitle;
                                    topicUID =
                                        this.getTopicUIDByTitle(topicTitle);

                                    if (topicUID) {
                                        this.completedTopicsTimestamps.push({
                                            topicUID: topicUID,
                                            topicTitle: topicTitle,
                                            startTime: new Date().getTime(),
                                            questionsAnswered: 0,
                                        });

                                        // Check for stimulus and emit event if one exists
                                        const stimulusData =
                                            this.getStimulusForTopic(topicUID);
                                        if (stimulusData) {
                                            this.stimulusDisplaySubject.next({
                                                stimuli_url:
                                                    stimulusData.stimuli_url,
                                                stimulusImages:
                                                    stimulusData.stimulusImages,
                                                stimulusMedia:
                                                    stimulusData.stimulusMedia,
                                                topicTitle: topicTitle,
                                            });
                                            this.startStimulusActivity();
                                        }
                                    }
                                }

                                // Show the quant question defined for this
                                // topic in the discussion guide (if any). Each
                                // topic's quant is shown once, when it starts.
                                if (
                                    topicUID &&
                                    !this._quantShownTopicUIDs.has(topicUID)
                                ) {
                                    const startedTopic =
                                        this.getTopicByUID(topicUID);
                                    if (startedTopic?.quant) {
                                        this._quantShownTopicUIDs.add(topicUID);
                                        this.triggerQuantQuestion(
                                            startedTopic.quant,
                                            startedTopic.title ||
                                                startedTopic.key ||
                                                parameters?.['topicTitle'] ||
                                                '',
                                        );
                                    }
                                }

                                // Send tool response
                                if (toolCallId) {
                                    this.sendToolResponse(toolCallId, {
                                        status: 'success'
                                    });
                                }
                            }
                            if (toolName === 'markTopicCompleted') {
                                Sentry.addBreadcrumb({
                                    message: 'Topic completed tool call (legacy)',
                                    category: 'elevenlabs.topic',
                                    level: 'info',
                                    data: {
                                        topicUID: parameters?.['UID'],
                                        topicTitle: parameters?.['topic_title'],
                                        questionsAnswered:
                                            parameters?.['questionsAnswered'],
                                        completedTopicsCount:
                                            this.completedTopics.length,
                                    },
                                });

                                LogRocket.track('markTopicCompleted', {
                                    parameters:
                                        msgObj['client_tool_call'][
                                            'parameters'
                                        ],
                                });

                                // Legacy tool - just handle the call, no response needed

                                // Check for UID first, then fall back to title
                                if (
                                    msgObj['client_tool_call']['parameters'] &&
                                    msgObj['client_tool_call']['parameters'][
                                        'UID'
                                    ]
                                ) {
                                    const topicUID =
                                        msgObj['client_tool_call'][
                                            'parameters'
                                        ]['UID'];

                                    // Get topic details from discussion guide using UID
                                    const topicData =
                                        this.getTopicByUID(topicUID);
                                    if (!topicData) {
                                        console.warn(
                                            'Topic not found for UID:',
                                            topicUID,
                                        );
                                        return;
                                    }

                                    const topicTitle =
                                        topicData.title ||
                                        topicData.key ||
                                        'Discussion Topic';

                                    // Find the topic in completedTopicsTimestamps
                                    const topic =
                                        this.completedTopicsTimestamps.find(
                                            (t) => t.topicUID === topicUID,
                                        );

                                    if (topic) {
                                        topic.endTime = new Date().getTime();
                                        topic.questionsAnswered =
                                            msgObj['client_tool_call'][
                                                'parameters'
                                            ].questionsAnswered || 0;
                                    }

                                    // Add to completed topics list
                                    const existingCompleted =
                                        this.completedTopics.find(
                                            (t) => t.topicUID === topicUID,
                                        );

                                    if (!existingCompleted) {
                                        this.completedTopics.push({
                                            topicUID: topicUID,
                                            topicTitle: topicTitle,
                                            questionsAnswered:
                                                msgObj['client_tool_call'][
                                                    'parameters'
                                                ].questionsAnswered || 0,
                                            completionNotes:
                                                msgObj['client_tool_call'][
                                                    'parameters'
                                                ].completionNotes || '',
                                            timestamp: new Date().getTime(),
                                        });
                                    }

                                    // Close stimulus modal when topic is completed
                                    this.stimulusDisplaySubject.next(null);
                                    this.clearStimulusActivity();

                                    // Update participant progress indicator
                                    this.updateTopicProgress();
                                } else if (
                                    msgObj['client_tool_call']['parameters'] &&
                                    msgObj['client_tool_call']['parameters'][
                                        'topic_title'
                                    ]
                                ) {
                                    // Fallback to title-based lookup for backward compatibility
                                    const topicTitle =
                                        msgObj['client_tool_call'][
                                            'parameters'
                                        ]['topic_title'];
                                    const topicUID =
                                        this.getTopicUIDByTitle(topicTitle);

                                    if (topicUID) {
                                        const topic =
                                            this.completedTopicsTimestamps.find(
                                                (t) => t.topicUID === topicUID,
                                            );
                                        if (topic) {
                                            topic.endTime =
                                                new Date().getTime();
                                            topic.questionsAnswered =
                                                msgObj['client_tool_call'][
                                                    'parameters'
                                                ].questionsAnswered || 0;
                                        }

                                        // Add to completed topics list if not already there
                                        const existingCompleted =
                                            this.completedTopics.find(
                                                (t) => t.topicUID === topicUID,
                                            );

                                        if (!existingCompleted) {
                                            this.completedTopics.push({
                                                topicUID: topicUID,
                                                topicTitle: topicTitle,
                                                questionsAnswered:
                                                    msgObj['client_tool_call'][
                                                        'parameters'
                                                    ].questionsAnswered || 0,
                                                completionNotes:
                                                    msgObj['client_tool_call'][
                                                        'parameters'
                                                    ].completionNotes || '',
                                                timestamp: new Date().getTime(),
                                            });
                                        }

                                        // Close stimulus modal when topic is completed
                                        this.stimulusDisplaySubject.next(null);
                                        this.clearStimulusActivity();

                                        // Update participant progress indicator
                                        this.updateTopicProgress();
                                    }
                                }
                            }
                            if (toolName === 'markTopicCompleted2') {
                                Sentry.addBreadcrumb({
                                    message: 'Topic completed tool call',
                                    category: 'elevenlabs.topic',
                                    level: 'info',
                                    data: {
                                        topicUID: parameters?.['UID'],
                                        topicTitle: parameters?.['topic_title'],
                                        questionsAnswered:
                                            parameters?.['questionsAnswered'],
                                        completedTopicsCount:
                                            this.completedTopics.length,
                                    },
                                });

                                LogRocket.track('markTopicCompleted', {
                                    parameters:
                                        msgObj['client_tool_call'][
                                            'parameters'
                                        ],
                                });
                                // Check for UID first, then fall back to title
                                if (
                                    msgObj['client_tool_call']['parameters'] &&
                                    msgObj['client_tool_call']['parameters'][
                                        'UID'
                                    ]
                                ) {
                                    const topicUID =
                                        msgObj['client_tool_call'][
                                            'parameters'
                                        ]['UID'];

                                    // Get topic details from discussion guide using UID
                                    const topicData =
                                        this.getTopicByUID(topicUID);
                                    if (!topicData) {
                                        console.warn(
                                            'Topic not found for UID:',
                                            topicUID,
                                        );
                                        // Send error response
                                        if (toolCallId) {
                                            this.sendToolResponse(toolCallId, {
                                                status: 404,
                                                message: 'Topic not found'
                                            }, true);
                                        }
                                        return;
                                    }

                                    const topicTitle =
                                        topicData.title ||
                                        topicData.key ||
                                        'Discussion Topic';

                                    // Find the topic in completedTopicsTimestamps
                                    const topic =
                                        this.completedTopicsTimestamps.find(
                                            (t) => t.topicUID === topicUID,
                                        );

                                    if (topic) {
                                        topic.endTime = new Date().getTime();
                                        topic.questionsAnswered =
                                            msgObj['client_tool_call'][
                                                'parameters'
                                            ].questionsAnswered || 0;
                                    }

                                    // Add to completed topics list
                                    const existingCompleted =
                                        this.completedTopics.find(
                                            (t) => t.topicUID === topicUID,
                                        );

                                    if (!existingCompleted) {
                                        this.completedTopics.push({
                                            topicUID: topicUID,
                                            topicTitle: topicTitle,
                                            questionsAnswered:
                                                msgObj['client_tool_call'][
                                                    'parameters'
                                                ].questionsAnswered || 0,
                                            completionNotes:
                                                msgObj['client_tool_call'][
                                                    'parameters'
                                                ].completionNotes || '',
                                            timestamp: new Date().getTime(),
                                        });
                                    }

                                    // Close stimulus modal when topic is completed
                                    this.stimulusDisplaySubject.next(null);
                                    this.clearStimulusActivity();

                                    // Update participant progress indicator
                                    this.updateTopicProgress();

                                    // Stop visual analysis and deliver summary
                                    if (this.visualAnalysisService.isEnabled()) {
                                        this.visualAnalysisService.stopTopicAnalysis(topicUID);
                                    }

                                    // Send tool response with next topic details
                                    if (toolCallId) {
                                        const nextTopic = this.getNextTopic(topicUID);
                                        if (nextTopic) {
                                            const nextTopicStimulusInfo = nextTopic.UID
                                                ? this.getStimulusInfo(nextTopic.UID)
                                                : { has_stimulus: false, type: null };
                                            const responsePayload = {
                                                status: 200,
                                                message: 'Next topic retrieved successfully',
                                                next_topic_title: nextTopic.title || nextTopic.key || 'Discussion Topic',
                                                next_topic_content: this.formatTopicContent(nextTopic),
                                                next_topic_uid: nextTopic.UID,
                                                next_topic_stimulus: nextTopicStimulusInfo
                                            };
                                            this.sendToolResponse(toolCallId, responsePayload);
                                             Sentry.addBreadcrumb({
                                                message: 'Sent next topic details',
                                                category: 'elevenlabs.dynamic_variables',
                                                level: 'info',
                                                data: {
                                                    nextTopicTitle: responsePayload.next_topic_title,
                                                    nextTopicUid: responsePayload.next_topic_uid,
                                                    nextTopicStimulus: responsePayload.next_topic_stimulus
                                                }
                                            });
                                        } else {
                                            // No next topic (end of interview)
                                            this.sendToolResponse(toolCallId, {
                                                status: 200,
                                                message: 'Interview complete',
                                                complete: true
                                            });
                                        }
                                    }
                                } else if (
                                    msgObj['client_tool_call']['parameters'] &&
                                    msgObj['client_tool_call']['parameters'][
                                        'topic_title'
                                    ]
                                ) {
                                    // Fallback to title-based lookup for backward compatibility
                                    const topicTitle =
                                        msgObj['client_tool_call'][
                                            'parameters'
                                        ]['topic_title'];
                                    const topicUID =
                                        this.getTopicUIDByTitle(topicTitle);

                                    if (topicUID) {
                                        const topic =
                                            this.completedTopicsTimestamps.find(
                                                (t) => t.topicUID === topicUID,
                                            );
                                        if (topic) {
                                            topic.endTime =
                                                new Date().getTime();
                                            topic.questionsAnswered =
                                                msgObj['client_tool_call'][
                                                    'parameters'
                                                ].questionsAnswered || 0;
                                        }

                                        // Add to completed topics list if not already there
                                        const existingCompleted =
                                            this.completedTopics.find(
                                                (t) => t.topicUID === topicUID,
                                            );

                                        if (!existingCompleted) {
                                            this.completedTopics.push({
                                                topicUID: topicUID,
                                                topicTitle: topicTitle,
                                                questionsAnswered:
                                                    msgObj['client_tool_call'][
                                                        'parameters'
                                                    ].questionsAnswered || 0,
                                                completionNotes:
                                                    msgObj['client_tool_call'][
                                                        'parameters'
                                                    ].completionNotes || '',
                                                timestamp: new Date().getTime(),
                                            });
                                        }

                                        // Close stimulus modal when topic is completed
                                        this.stimulusDisplaySubject.next(null);
                                        this.clearStimulusActivity();

                                        // Update participant progress indicator
                                        this.updateTopicProgress();

                                        // Stop visual analysis and deliver summary
                                        if (this.visualAnalysisService.isEnabled() && topicUID) {
                                            this.visualAnalysisService.stopTopicAnalysis(topicUID);
                                        }

                                        // Send tool response with next topic details
                                        if (toolCallId) {
                                            const nextTopic = this.getNextTopic(topicUID);
                                            if (nextTopic) {
                                                const nextTopicStimulusInfo = nextTopic.UID
                                                    ? this.getStimulusInfo(nextTopic.UID)
                                                    : { has_stimulus: false, type: null };
                                                this.sendToolResponse(toolCallId, {
                                                    status: 200,
                                                    message: 'Next topic retrieved successfully',
                                                    next_topic_title: nextTopic.title || nextTopic.key || 'Discussion Topic',
                                                    next_topic_content: this.formatTopicContent(nextTopic),
                                                    next_topic_uid: nextTopic.UID,
                                                    next_topic_stimulus: nextTopicStimulusInfo
                                                });
                                            } else {
                                                // No next topic (end of interview)
                                                this.sendToolResponse(toolCallId, {
                                                    status: 200,
                                                    message: 'Interview complete',
                                                    complete: true
                                                });
                                            }
                                        }
                                    } else {
                                        // Topic UID not found for the given title
                                        if (toolCallId) {
                                            this.sendToolResponse(toolCallId, {
                                                status: 404,
                                                message: 'Topic not found by title'
                                            }, true);
                                        }
                                    }
                                } else {
                                    // No UID or topic_title provided
                                    if (toolCallId) {
                                        this.sendToolResponse(toolCallId, {
                                            status: 400,
                                            message: 'Missing required parameter: UID or topic_title'
                                        }, true);
                                    }
                                }
                            }

                            if (toolName === 'handleMissingStimulus') {
                                Sentry.addBreadcrumb({
                                    message:
                                        'Handle missing stimulus tool call',
                                    category: 'elevenlabs.stimulus',
                                    level: 'info',
                                    data: {
                                        topicUID: parameters?.['UID'],
                                    },
                                });

                                LogRocket.track('handleMissingStimulus', {
                                    parameters:
                                        msgObj['client_tool_call'][
                                            'parameters'
                                        ],
                                });

                                const topicUID =
                                    msgObj['client_tool_call']['parameters'][
                                        'UID'
                                    ];

                                if (topicUID) {
                                    this.handleTopicTracking(topicUID);

                                    // Send success response
                                    if (toolCallId) {
                                        this.sendToolResponse(toolCallId, {
                                            status: 200,
                                            message: 'Missing stimulus handled successfully'
                                        });
                                    }
                                } else {
                                    // No UID provided
                                    if (toolCallId) {
                                        this.sendToolResponse(toolCallId, {
                                            status: 400,
                                            message: 'Missing required parameter: UID'
                                        }, true);
                                    }
                                }
                            }
                        } catch (error) {
                            hasError = true;
                            Sentry.captureException(error, {
                                tags: {
                                    component: 'ai-moderator',
                                    event_type: 'client_tool_call',
                                    provider: 'elevenlabs',
                                },
                                extra: {
                                    toolName:
                                        msgObj['client_tool_call']?.[
                                            'tool_name'
                                        ],
                                    parameters:
                                        msgObj['client_tool_call']?.[
                                            'parameters'
                                        ],
                                },
                            });
                        }
                        break;
                    case 'agent_response_correction':
                        try {
                            const originalResponse =
                                msgObj['agent_response_correction_event'][
                                    'original_agent_response'
                                ];
                            const correctedResponse =
                                msgObj['agent_response_correction_event'][
                                    'corrected_agent_response'
                                ];

                            Sentry.addBreadcrumb({
                                message: 'Agent response correction received',
                                category: 'elevenlabs.response_correction',
                                level: 'info',
                                data: {
                                    originalLength:
                                        originalResponse?.length || 0,
                                    correctedLength:
                                        correctedResponse?.length || 0,
                                    conversationLength:
                                        this.conversation.length,
                                },
                            });

                            const index = this.conversation.findIndex(
                                (msg) =>
                                    msg.role === 'assistant' &&
                                    msg.content === originalResponse,
                            );

                            if (index !== -1) {
                                this.conversation[index].content =
                                    correctedResponse;

                                Sentry.addBreadcrumb({
                                    message:
                                        'Agent response corrected in conversation',
                                    category: 'elevenlabs.response_correction',
                                    level: 'info',
                                    data: {
                                        conversationIndex: index,
                                        originalResponse: originalResponse,
                                        correctedResponse: correctedResponse,
                                    },
                                });
                            } else {
                                Sentry.addBreadcrumb({
                                    message:
                                        'Original response not found for correction',
                                    category: 'elevenlabs.response_correction',
                                    level: 'warning',
                                    data: {
                                        originalResponse: originalResponse,
                                        conversationLength:
                                            this.conversation.length,
                                    },
                                });
                            }
                        } catch (error) {
                            hasError = true;
                            Sentry.captureException(error, {
                                tags: {
                                    component: 'ai-moderator',
                                    event_type: 'agent_response_correction',
                                    provider: 'elevenlabs',
                                },
                                extra: {
                                    originalResponse:
                                        msgObj[
                                            'agent_response_correction_event'
                                        ]?.['original_agent_response'],
                                    correctedResponse:
                                        msgObj[
                                            'agent_response_correction_event'
                                        ]?.['corrected_agent_response'],
                                },
                            });
                        }
                        break;
                    default:
                        this.logUnknownElevenLabsEvent(msgObj);
                        break;
                }

                // Track successful event processing only if no errors occurred
                if (!hasError) {
                    Sentry.addBreadcrumb({
                        message: 'ElevenLabs event processed successfully',
                        category: 'elevenlabs.success',
                        level: 'info',
                        data: {
                            eventType: msgObj['type'],
                            processingTime: Date.now() - msgObj.startTime,
                        },
                    });
                }
            },
        );
    }

    private logUnknownElevenLabsEvent(msgObj: any): void {
        Sentry.addBreadcrumb({
            message: 'Unknown ElevenLabs event type received',
            category: 'elevenlabs.unknown',
            level: 'warning',
            data: {
                eventType: msgObj['type'],
                eventKeys: Object.keys(msgObj),
            },
        });

        Sentry.captureMessage('Unknown ElevenLabs event type', {
            level: 'warning',
            tags: {
                component: 'ai-moderator',
                event_type: msgObj['type'] || 'undefined',
                provider: 'elevenlabs',
            },
            extra: {
                fullEvent: msgObj,
            },
        });
    }

    handleFirstAiAudioComplete() {
        if (!this.participantService.micEnabled && !this._hasAiSpokenOnce) {
            this.participantService.togglemic();
        }
        this._hasAiSpokenOnce = true;
        // The AI has spoken; the greeting arrived, so stand the watchdog down.
        this.clearGreetingWatchdog();
    }

    private waitingForAudioCompletion = false;
    private callEnding = false;

    private waitForAllAudioToComplete() {
        // Prevent multiple simultaneous audio completion waits
        if (this.waitingForAudioCompletion) {
            console.log(
                '[AudioCompletion] Already waiting for audio completion',
            );
            return;
        }

        this.waitingForAudioCompletion = true;
        console.log(
            '[AudioCompletion] Starting Web Audio API-based completion detection',
        );

        // Use Web Audio API detection instead of trying to track individual sources
        this.waitForAiToStopSpeaking();
    }

    private waitForAiToStopSpeaking() {
        console.log(
            `[AudioCompletion] Current AI speaking state: ${this.isAiSpeaking.value}`,
        );

        if (this.isAiSpeaking.value) {
            // AI is currently speaking, wait for it to stop
            console.log(
                '[AudioCompletion] AI is speaking, waiting for silence...',
            );

            const subscription = this.isAiSpeaking
                .pipe(distinctUntilChanged(), takeUntil(this.unsubscribe$))
                .subscribe((isSpeaking) => {
                    if (!isSpeaking) {
                        console.log(
                            '[AudioCompletion] AI stopped speaking, waiting for final buffer...',
                        );
                        subscription.unsubscribe();

                        // Wait a bit longer to ensure any final audio has finished playing
                        setTimeout(() => {
                            console.log(
                                '[AudioCompletion] Audio completion detected - ending call',
                            );
                            this.waitingForAudioCompletion = false;
                            this.endCall();
                        }, 2500); // 2.5 second buffer after AI stops speaking to account for audio processing delays
                    }
                });

            // Safety timeout in case the audio detection fails
            setTimeout(() => {
                console.log(
                    '[AudioCompletion] Safety timeout reached - ending call',
                );
                subscription.unsubscribe();
                this.waitingForAudioCompletion = false;
                this.endCall();
            }, 10000); // 10 second maximum wait
        } else {
            // AI is not currently speaking, wait briefly in case audio is about to start
            console.log(
                '[AudioCompletion] AI not speaking, waiting briefly for potential audio...',
            );
            setTimeout(() => {
                if (this.isAiSpeaking.value) {
                    // Audio started, wait for it to complete
                    this.waitForAiToStopSpeaking();
                } else {
                    // No audio detected, end the call
                    console.log(
                        '[AudioCompletion] No AI audio detected - ending call',
                    );
                    this.waitingForAudioCompletion = false;
                    this.endCall();
                }
            }, 2000); // 2 seconds to wait for potential audio
        }
    }

    async endCall() {
        // Prevent multiple simultaneous endCall attempts
        if (this.callEnding) {
            return;
        }

        this.callEnding = true;
        console.log('Starting endCall process');

        // The transcript POST used to be the gate for ALL teardown: no error
        // callback, so one failed request lost the transcript permanently and
        // left the participant sitting in a dead room that could never end
        // (callEnding stayed latched). Retry the POST, and tear the session
        // down whether or not it ultimately succeeds.
        let transcript$;
        try {
            transcript$ = this.sendTranscript();
        } catch (error: any) {
            // sendTranscript() can throw synchronously (discussion-guide
            // JSON.parse) — teardown must still happen.
            console.error('Failed to build transcript during endCall', error);
            Sentry.captureException(error, {
                tags: {
                    component: 'ai-moderator',
                    event_type: 'transcript_build_failed',
                    provider: this._provider,
                },
            });
            this.finishEndCall();
            return;
        }

        transcript$
            .pipe(
                retry({
                    count: 2,
                    delay: (_error, retryCount) => timer(2000 * retryCount),
                }),
            )
            .subscribe({
                next: () => {
                    this.finishEndCall();
                },
                error: (error: any) => {
                    console.error(
                        'Failed to send transcript during endCall (after retries)',
                        error,
                    );
                    LogRocket.track('transcript-send-failed', {
                        stage: 'end-call',
                    });
                    Sentry.captureException(error, {
                        tags: {
                            component: 'ai-moderator',
                            event_type: 'transcript_send_failed',
                            provider: this._provider,
                        },
                        extra: { conversationId: this._conversationId },
                    });
                    this.finishEndCall();
                },
            });
    }

    private async finishEndCall(): Promise<void> {
        // Belt-and-suspenders: ensure no reconnect backoff survives teardown.
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }
        try {
            if (this.callObject) {
                this.callObject.sendAppMessage(
                    {
                        type: 'end-session',
                        message: 'The AI moderator has ended the session',
                        timestamp: new Date().getTime(),
                    },
                    '*',
                );

                const participants = this.callObject.participants();
                if (participants) {
                    Object.keys(participants).forEach((participantId) => {
                        if (participantId !== 'local') {
                            this.callObject?.updateParticipant(participantId, {
                                eject: true,
                            });
                        }
                    });
                }
            }
        } catch (error: any) {
            console.error(
                'Error ending session for all participants:',
                error,
            );
        }

        this.closeWebSocket(false, this.WS_CLOSE.NORMAL, 'end-call');
        this.removeResumeListeners();
        this.clearStimulusActivity();
        this.closeAiPlaybackContext();

        // Release retained media-tracking sessions/events now that the
        // transcript payload has already been built and sent (payload
        // unchanged — this only frees the in-memory copy).
        this.videoTrackingService.clearSessions();
        this.audioTrackingService.clearSessions();
        // Interview is over — drop the persisted resume state so a later load
        // in the same tab/room starts fresh instead of resuming a finished
        // interview.
        this.clearResumeState();
        if (this.visualAnalysisService.isEnabled()) {
            this.visualAnalysisService.destroy();
        }

        if (this.elevenLabsConversation) {
            await this.elevenLabsConversation.endSession();
            this.elevenLabsConversation = null;
        }

        if (this.callObject) {
            this.callObject.leave();
        }

        this.aiModerator.next(false);
    }

    handleWebSocketReconnect() {
        // The interview is ending / the participant has left — do not schedule a
        // reconnect. Otherwise the backoff timer fires post-teardown, re-mints a
        // URL, and opens a zombie conversation no watchdog ever closes.
        if (this._endRequested || this.callEnding) {
            return;
        }

        // Check if we've exceeded the maximum reconnect attempts
        if (this.reconnectAttempts >= this.maxReconnectAttempts) {
            console.log('Max WebSocket reconnect attempts reached. Giving up.');
            this.connectionState$.next('failed');

            // The real fix-impact signal: recovery exhausted, the participant is
            // stuck. This % should trend down as the reconnect/resume fixes land.
            LogRocket.track('ai-connection-failed', {
                reconnectAttempts: this.reconnectAttempts,
            });

            Sentry.captureMessage(
                'AI moderator WebSocket reconnection exhausted',
                {
                    level: 'error',
                    tags: {
                        component: 'ai-moderator',
                        event_type: 'ws_reconnect_exhausted',
                        provider: this._provider,
                    },
                    extra: {
                        conversationId: this._conversationId,
                        reconnectAttempts: this.reconnectAttempts,
                        maxReconnectAttempts: this.maxReconnectAttempts,
                    },
                },
            );
            return;
        }

        this.connectionState$.next('reconnecting');

        // Use exponential backoff for reconnection delay, but let setupWebSocket() handle the attempt limit
        let delay = Math.min(5000 * Math.pow(2, this.reconnectAttempts), 30000); // Exponential backoff, max 30s
        this.reconnectAttempts++;

        console.log(
            `Attempting WebSocket reconnect (attempt ${this.reconnectAttempts + 1}/${this.maxReconnectAttempts})`,
        );

        this.reconnectTimer = setTimeout(() => {
            this.reconnectTimer = null;
            // The signed URL embeds an ElevenLabs conversation_signature that
            // expires (abnormal close 1006, or close 3000 "Conversation_signature
            // is either invalid or has expired") and is consumed by the failed
            // connection. Re-mint a fresh signed URL before reconnecting so the
            // loop can actually recover instead of reusing the same dead URL.
            // Mirrors the mint path in setup.component, including its timeout —
            // the mint endpoint can hang up to its ~30s gateway limit, and a
            // hung request here left the participant stuck on "Reconnecting…"
            // forever (the error path below never ran, so no reconnect).
            //
            // Use the agent id that minted the working URL (signedUrlAgentId).
            // Flag-agent rooms have no aiModeratorProjectAgentId, so re-minting
            // with it sent an empty ai-agent-id — the backend returned 200 with
            // no ai-signed-url and the loop reused the dead URL until it gave
            // up. Falls back to the project id if the resolved id is unset.
            this.apiService
                .getRoomDetails({
                    'ai-agent-id':
                        this.signedUrlAgentId || this.aiModeratorProjectAgentId,
                })
                .pipe(timeout({ each: this.SIGNED_URL_REMINT_TIMEOUT_MS }))
                .subscribe({
                    next: (data: any) => {
                        const refreshedUrl = data?.['ai-signed-url'];
                        if (refreshedUrl) {
                            const changed =
                                refreshedUrl !== this.getSignedUrl();
                            console.log(
                                `Refreshed signed URL before reconnect (changed: ${changed}).`,
                            );
                            this.setSignedUrl(refreshedUrl);
                        } else {
                            console.warn(
                                'Reconnect signed-URL refresh returned no ai-signed-url; reusing existing URL.',
                            );
                        }
                        this.setupWebSocket();
                    },
                    error: (err: any) => {
                        console.error(
                            'Failed to refresh signed URL before reconnect; reusing existing URL.',
                            err,
                        );
                        this.setupWebSocket();
                    },
                });
        }, delay);
    }

    updateConversation(msgObj: any) {
        const timestamp = new Date().toISOString();
        msgObj.timestamp = timestamp;
        this.conversation.push(msgObj);
    }

    private buildTranscriptData() {
        let name = this.participantService.localParticipant?.userName || 'User';

        if (this._interviewStartTimestamp !== null) {
            const interviewDuration =
                Date.now() - this._interviewStartTimestamp;
            LogRocket.track('interview-duration-minutes', {
                durationMinutes: interviewDuration / (1000 * 60),
            });
        }

        this.transcript = '';
        this.transcriptTimestamp = '';

        this.conversation.forEach((item: any) => {
            const userName =
                item.role === 'user'
                    ? this.userService.userName
                    : 'AI Moderator';
            this.transcript += `[${userName}:] ${item.content} \n`;
            this.transcriptTimestamp += `${item.timestamp} [${userName}:] ${item.content} \n`;
        });

        // Get media analytics summaries before sending
        const videoAnalytics = this.getVideoAnalyticsSummary();
        const audioAnalytics = this.getAudioAnalyticsSummary();

        const discussionGuideToSend = JSON.parse(this.rawDiscussionGuide).map(
            (topic: any) => {
                const { probingNotes, ...topicWithoutProbingNotes } = topic;

                if (topicWithoutProbingNotes.stimulusImages) {
                    topicWithoutProbingNotes.stimulusImages =
                        topicWithoutProbingNotes.stimulusImages.map(
                            (image: any) => {
                                const {
                                    description,
                                    ...imageWithoutDescription
                                } = image;
                                return imageWithoutDescription;
                            },
                        );
                }

                if (topicWithoutProbingNotes.stimulusMedia) {
                    topicWithoutProbingNotes.stimulusMedia =
                        topicWithoutProbingNotes.stimulusMedia.map(
                            (media: any) => {
                                const {
                                    description,
                                    ...mediaWithoutDescription
                                } = media;
                                return mediaWithoutDescription;
                            },
                        );
                }

                return topicWithoutProbingNotes;
            },
        );

        return {
            recordingId: this.recordingService.recordingId.value as string,
            transcript: this.transcript,
            name,
            discussionGuide: JSON.stringify(discussionGuideToSend),
            conversationId: this._conversationId,
            userId: this._userId,
            completedTopics: this.completedTopicsTimestamps,
            transcriptTimestamp: this.transcriptTimestamp,
            videoAnalytics,
            audioAnalytics,
            feedback: this.feedbackItems,
            quantResponses: this.quantResponses,
        };
    }

    // Recording id whose transcript has already been POSTed. A normal AI
    // completion sends the transcript from BOTH endCall() and the complete
    // screen, and the API does not dedupe on recording_id — so without this
    // guard the same interview lands as two rows. Keyed by recordingId (not a
    // plain flag) so a later interview in the same tab (spillover/resume, this
    // service is a root singleton) still sends.
    private _sentTranscriptRecordingId: string | null = null;

    sendTranscript(): Observable<any> {
        const data = this.buildTranscriptData();

        // Already sent for this recording — skip the duplicate POST but still
        // emit so subscribers (e.g. the complete screen) run their next path.
        if (
            data.recordingId &&
            data.recordingId === this._sentTranscriptRecordingId
        ) {
            return of(null);
        }

        return this.apiService
            .sendTranscript(
                data.recordingId,
                data.transcript,
                data.name,
                data.discussionGuide,
                data.conversationId,
                data.userId,
                data.completedTopics,
                data.transcriptTimestamp,
                data.videoAnalytics,
                data.feedback,
                data.quantResponses,
                data.audioAnalytics,
            )
            .pipe(
                // Mark sent only on a successful response, so if endCall()'s
                // POST fails all its retries the complete screen still acts as
                // the backup rather than being suppressed.
                tap(() => {
                    this._sentTranscriptRecordingId = data.recordingId;
                }),
            );
    }

    /**
     * Fire-and-forget transcript upload for page unload. A normal XHR started
     * in beforeunload is cancelled by the browser when the page tears down,
     * silently losing the interview's transcript; fetch keepalive survives
     * the unload.
     */
    sendTranscriptKeepalive(): void {
        try {
            const data = this.buildTranscriptData();

            // Skip only if a sendTranscript() POST for this recording already
            // SUCCEEDED — that success is the only thing that sets the guard.
            // We deliberately do NOT set the guard here: a keepalive is
            // fire-and-forget on unload, so its outcome can't be observed.
            // Marking it "sent" could suppress a genuine later save (e.g. the
            // participant cancels the unload prompt and then finishes) with no
            // confirmation anything was stored. Not setting it costs at worst a
            // duplicate row in that rare path — never a lost transcript.
            if (
                data.recordingId &&
                data.recordingId === this._sentTranscriptRecordingId
            ) {
                return;
            }

            this.apiService.sendTranscriptKeepalive(
                data.recordingId,
                data.transcript,
                data.name,
                data.discussionGuide,
                data.conversationId,
                data.userId,
                data.completedTopics,
                data.transcriptTimestamp,
                data.videoAnalytics,
                data.feedback,
                data.quantResponses,
                data.audioAnalytics,
            );
        } catch (error) {
            console.error('Failed to build keepalive transcript', error);
        }
    }

    clearAudioTrackRetry() {
        if (this.audioTrackRetryTimer) {
            clearTimeout(this.audioTrackRetryTimer);
            this.audioTrackRetryTimer = null;
        }
    }

    stopAudioProcessing() {
        if (this.workletNode) {
            console.log('Stopping AudioWorklet processing...');
            this.workletNode.disconnect();
            this.workletNode = null;
        }

        if (this.audioContext) {
            console.log('Closing AudioContext...');
            this.audioContext.close();
            this.audioContext = null;
        }

        this.accumulatedSamples = [];
        this.accumulatedLength = 0;
        this._boundAudioTrackId = null;
    }

    private startStimulusActivity() {
        this.clearStimulusActivity();

        if (this.provider === 'elevenlabs' && this.isWebSocketOpen()) {
            this._stimulusActivityInterval = setInterval(() => {
                if (this.provider === 'elevenlabs' && this.isWebSocketOpen()) {
                    this.ws?.send(JSON.stringify({ type: 'user_activity' }));
                }
            }, 15000); // 15 seconds - safely under the 20s turn timer
        }
    }

    private clearStimulusActivity() {
        if (this._stimulusActivityInterval) {
            clearInterval(this._stimulusActivityInterval);
            this._stimulusActivityInterval = null;
        }
    }

    private sendContextualUpdate(message: string) {
        const wsOpen = this.isWebSocketOpen();
        console.log(
            `VisualAnalysis: sendContextualUpdate called - wsOpen=${wsOpen}, provider=${this.provider}, message="${message.substring(0, 100)}..."`,
        );
        if (!wsOpen) {
            console.warn('VisualAnalysis: WebSocket NOT open, contextual update dropped');
            return;
        }

        if (this.provider === 'elevenlabs') {
            this.ws?.send(
                JSON.stringify({
                    type: 'contextual_update',
                    text: message,
                }),
            );
        }
    }

    private generateStimulusDescription(stimulusData: {
        stimuli_url?: string;
        stimulusImages?: any[];
        stimulusMedia?: any[];
    }): {
        url: string;
        description: string;
        isMultiple: boolean;
        isVideo: boolean;
    } {
        // Handle new unified stimulusMedia structure first
        if (
            stimulusData.stimulusMedia &&
            stimulusData.stimulusMedia.length > 0
        ) {
            const videos = stimulusData.stimulusMedia.filter(
                (media: any) => media.type === 'video',
            );
            const images = stimulusData.stimulusMedia.filter(
                (media: any) => media.type === 'image',
            );

            if (videos.length > 0) {
                const descriptions = videos
                    .map(
                        (vid: any) =>
                            vid.description || vid.metadata?.description || '',
                    )
                    .filter((desc: string) => desc)
                    .join(' and ');

                return {
                    url: videos[0].url || 'video',
                    description: descriptions || 'Video stimulus',
                    isMultiple: videos.length > 1,
                    isVideo: true,
                };
            } else if (images.length > 0) {
                const descriptions = images
                    .map((img: any) => img.description || img.desc)
                    .filter((desc: string) => desc)
                    .join(' and ');

                return {
                    url: 'multiple_images',
                    description: descriptions || 'Multiple stimulus images',
                    isMultiple: images.length > 1,
                    isVideo: false,
                };
            }
        }
        // Fallback to old structure for backwards compatibility
        else if (
            stimulusData.stimulusImages &&
            stimulusData.stimulusImages.length > 0
        ) {
            const descriptions = stimulusData.stimulusImages
                .map((img) => img.description || img.desc)
                .filter((desc) => desc)
                .join(' and ');

            return {
                url: 'multiple_images',
                description: descriptions || 'Multiple stimulus images',
                isMultiple: stimulusData.stimulusImages.length > 1,
                isVideo: false,
            };
        } else if (stimulusData.stimuli_url) {
            const isVideo = this.isVideoUrl(stimulusData.stimuli_url);
            return {
                url: stimulusData.stimuli_url,
                description: '',
                isMultiple: false,
                isVideo: isVideo,
            };
        }
        return { url: '', description: '', isMultiple: false, isVideo: false };
    }

    private isVideoUrl(url: string): boolean {
        const videoExtensions = [
            '.mp4',
            '.webm',
            '.ogg',
            '.mov',
            '.avi',
            '.mkv',
            '.m3u8',
        ];
        const videoPatterns = [
            'youtube.com',
            'youtu.be',
            'vimeo.com',
            'dailymotion.com',
        ];
        const lowerUrl = url.toLowerCase();

        return (
            videoExtensions.some((ext) => lowerUrl.includes(ext)) ||
            videoPatterns.some((pattern) => lowerUrl.includes(pattern))
        );
    }

    sendStimulusContextUpdate(
        stimulusUrl: string,
        topicTitle: string,
        description?: string,
        isMultiple?: boolean,
        isVideo?: boolean,
    ) {
        const mediaType = isVideo ? 'video' : 'image';
        const mediaText = isMultiple
            ? `stimulus ${mediaType}s`
            : `a stimulus ${mediaType}`;
        let contextMessage = `Participant is now viewing ${mediaText} for the "${topicTitle}" topic.`;

        if (description) {
            contextMessage += ` Description: ${description}`;
        } else if (isVideo) {
            contextMessage += ` The video is ${stimulusUrl.startsWith('http') ? 'available at: ' + stimulusUrl : 'embedded in the page'}`;
        } else {
            contextMessage += ` Description:`;
            // Check if it's a base64 encoded image
            if (
                stimulusUrl.startsWith('data:image/') ||
                stimulusUrl.match(/^[A-Za-z0-9+/=]+$/)
            ) {
                contextMessage +=
                    '. The image is displayed as an embedded image';
            } else {
                contextMessage += `. The image is available at: ${stimulusUrl}`;
            }
        }

        if (isVideo) {
            contextMessage +=
                '. IMPORTANT: Please wait for the participant to watch the video before asking any questions about this topic. You will receive a notification when they have finished viewing the video. You may briefly acknowledge that the video is being shown, but do not ask questions yet.';
        } else {
            contextMessage +=
                '. You can now reference what they are seeing in your questions and discussion.';
        }

        this.sendContextualUpdate(contextMessage);
    }

    sendStimulusRemovedUpdate(topicTitle?: string, isVideo?: boolean) {
        const mediaType = isVideo ? 'video' : 'image';
        const contextMessage = topicTitle
            ? `The stimulus ${mediaType} for "${topicTitle}" topic has been removed from view.`
            : `The stimulus ${mediaType} has been removed from view.`;

        this.sendContextualUpdate(contextMessage);
    }

    /**
     * Show the quant question defined for a topic in the discussion guide.
     * Mirrors the stimulus show path - pushes to the display subject, starts
     * the user_activity keep-alive so the agent does not time out / interrupt
     * while the widget is on screen, and tells the agent to wait for the
     * answer before asking about it.
     */
    private triggerQuantQuestion(
        quant: { question: string; config: QuantConfig },
        topicTitle: string,
    ) {
        const payload: QuantQuestion = {
            question: quant.question,
            topicTitle,
            config: quant.config,
        };
        this.quantDisplaySubject.next(payload);
        this.startStimulusActivity();
        this.sendQuantContextUpdate(payload);
    }

    sendQuantContextUpdate(quant: QuantQuestion) {
        const contextMessage =
            `A quant question is now on screen for the participant${quant.topicTitle ? ` during the "${quant.topicTitle}" topic` : ''}: "${quant.question}" ` +
            `They are ${this.describeQuantWidget(quant.config)}. IMPORTANT: Please wait for the participant to make their selection before asking about it. You will receive a notification with their answer. Do not ask questions about it yet.`;

        this.sendContextualUpdate(contextMessage);
    }

    private describeQuantWidget(config: QuantConfig): string {
        switch (config.format) {
            case 'scale':
                return `choosing a number from ${config.min} to ${config.max} on a slider`;
            case 'single':
                return `choosing one of: ${config.options.join(', ')}`;
            case 'multi':
                return `choosing one or more of: ${config.options.join(', ')}`;
        }
    }

    sendQuantAnswerUpdate(answer: QuantAnswer, topicTitle?: string) {
        // Sent as a user_message (not a contextual_update) so it triggers a
        // turn and the agent responds to the answer immediately, rather than
        // waiting for the (silent) participant to speak first.
        const rendered = this.renderQuantAnswer(answer);
        const message = `[The participant answered ${rendered} for the quant question "${answer.question}"${topicTitle ? ` (topic: "${topicTitle}")` : ''}. Please acknowledge their answer and ask a follow-up about why.]`;

        if (!this.isWebSocketOpen()) {
            console.warn('Quant: WebSocket NOT open, answer update dropped');
            return;
        }

        if (this.provider === 'elevenlabs') {
            this.ws?.send(
                JSON.stringify({
                    type: 'user_message',
                    text: message,
                }),
            );
        }
    }

    private renderQuantAnswer(answer: QuantAnswer): string {
        switch (answer.format) {
            case 'scale':
                // The range lives in the question text, so the number suffices.
                return `${answer.value}`;
            case 'single':
                return `"${answer.value}"`;
            case 'multi': {
                const values = Array.isArray(answer.value) ? answer.value : [];
                return values.length
                    ? values.map((v) => `"${v}"`).join(', ')
                    : 'nothing';
            }
        }
    }

    /**
     * POC: record the participant's quant answer. Stores it for the API POST,
     * notifies the agent of the choice, stops the keep-alive, and hides the
     * widget.
     */
    recordQuantResponse(answer: QuantAnswer, topicTitle: string) {
        this.quantResponses.push({
            question: answer.question,
            format: answer.format,
            value: answer.value,
            topicTitle,
            timestamp: Date.now(),
        });
        this.sendQuantAnswerUpdate(answer, topicTitle);
        this.clearStimulusActivity();
        this.quantDisplaySubject.next(null);
    }

    sendVideoEventUpdate(eventType: string, data: any, topicTitle?: string) {
        let contextMessage = '';

        switch (eventType) {
            case 'play':
                contextMessage = `Participant started playing the video${topicTitle ? ` for "${topicTitle}"` : ''} at ${data.currentTime || 0} seconds`;
                break;
            case 'pause':
                contextMessage = `Participant paused the video${topicTitle ? ` for "${topicTitle}"` : ''} at ${data.currentTime || 0} seconds`;
                break;
            case 'ended':
                contextMessage = `Participant finished watching the video${topicTitle ? ` for "${topicTitle}"` : ''}. Total watch time: ${data.totalWatchTime || 0} seconds. You may now proceed to ask questions about the video and this topic.`;
                // Store completed video session
                this.storeVideoSession(data.topicId);
                break;
            case 'seek':
                contextMessage = `Participant seeked in the video from ${data.from || 0} to ${data.to || 0} seconds`;
                break;
            case 'error':
                contextMessage = `Video playback error occurred: ${data.message || 'Unknown error'}. You may proceed to ask questions about this topic.`;
                break;
        }

        if (contextMessage) {
            this.sendContextualUpdate(contextMessage);
        }
    }

    /**
     * Audio stimulus playback updates. Mirrors sendVideoEventUpdate with audio
     * wording, and files the completed session into audioAnalytics — audio is
     * rendered by VideoPlayerComponent in mediaKind="audio" and tracked by
     * AudioTrackingService, the audio counterpart of the video path.
     *
     * 'stopped' is the clip-abandoned case (another clip started, or the
     * stimulus was removed); it stores the session without narrating anything to
     * the agent.
     */
    sendAudioEventUpdate(eventType: string, data: any, topicTitle?: string) {
        let contextMessage = '';

        switch (eventType) {
            case 'play':
                contextMessage = `Participant started playing the audio${topicTitle ? ` for "${topicTitle}"` : ''} at ${data.currentTime || 0} seconds`;
                break;
            case 'pause':
                contextMessage = `Participant paused the audio${topicTitle ? ` for "${topicTitle}"` : ''} at ${data.currentTime || 0} seconds`;
                break;
            case 'ended':
                contextMessage = `Participant finished listening to the audio${topicTitle ? ` for "${topicTitle}"` : ''}. Total listen time: ${data.totalWatchTime || 0} seconds. You may now proceed to ask questions about the audio and this topic.`;
                // Store completed audio session
                this.storeAudioSession(data.topicId);
                break;
            case 'seek':
                contextMessage = `Participant seeked in the audio from ${data.from || 0} to ${data.to || 0} seconds`;
                break;
            case 'stopped':
                // Clip abandoned part-way. File what was listened to; the agent
                // does not need a narration for a clip switch or a dismissal.
                this.storeAudioSession(data.topicId);
                break;
            case 'error':
                contextMessage = `Audio playback error occurred. You may proceed to ask questions about this topic.`;
                break;
        }

        if (contextMessage) {
            this.sendContextualUpdate(contextMessage);
        }
    }

    storeVideoSession(topicId?: string) {
        const currentSession = this.videoTrackingService.getCurrentSession();
        if (currentSession) {
            // Store the session
            this.videoAnalytics.sessions.push({
                ...currentSession,
                timestamp: Date.now(),
            });

            // Store topic-specific engagement if topicId provided
            if (topicId) {
                this.videoAnalytics.topicVideoEngagement[topicId] = {
                    sessionId: currentSession.sessionId,
                    metrics: currentSession.metrics,
                    eventCount: currentSession.events.length,
                    watchTime: currentSession.metrics.totalWatchTime,
                    completionRate: currentSession.metrics.completionPercentage,
                };
            }

            // Update aggregate metrics
            const allSessions = this.videoTrackingService.getAllSessions();
            if (allSessions.length > 0) {
                this.videoAnalytics.aggregateMetrics =
                    this.videoTrackingService.generateAnalytics(allSessions);
            }
        }
    }

    storeAudioSession(topicId?: string) {
        const currentSession = this.audioTrackingService.getCurrentSession();
        if (currentSession) {
            // Store the session
            this.audioAnalytics.sessions.push({
                ...currentSession,
                timestamp: Date.now(),
            });

            // Store topic-specific engagement if topicId provided
            if (topicId) {
                this.audioAnalytics.topicAudioEngagement[topicId] = {
                    sessionId: currentSession.sessionId,
                    metrics: currentSession.metrics,
                    eventCount: currentSession.events.length,
                    listenTime: currentSession.metrics.totalWatchTime,
                    completionRate: currentSession.metrics.completionPercentage,
                };
            }

            // Update aggregate metrics
            const allSessions = this.audioTrackingService.getAllSessions();
            if (allSessions.length > 0) {
                this.audioAnalytics.aggregateMetrics =
                    this.audioTrackingService.generateAnalytics(allSessions);
            }
        }
    }

    getAudioAnalyticsSummary() {
        return {
            totalAudioSessions: this.audioAnalytics.sessions.length,
            aggregateMetrics: this.audioAnalytics.aggregateMetrics,
            topicEngagement: this.audioAnalytics.topicAudioEngagement,
            sessions: this.audioAnalytics.sessions.map((session) => ({
                audioUrl: session.videoUrl,
                participantId: session.participantId,
                topicId: session.topicId,
                metrics: session.metrics,
                eventSummary: {
                    totalEvents: session.events?.length || 0,
                    playCount: session.metrics?.playCount || 0,
                    pauseCount: session.metrics?.pauseCount || 0,
                    seekCount: session.metrics?.seekCount || 0,
                },
                listenTime: session.metrics?.totalWatchTime || 0,
                completionRate: session.metrics?.completionPercentage || 0,
            })),
        };
    }

    getVideoAnalyticsSummary() {
        return {
            totalVideoSessions: this.videoAnalytics.sessions.length,
            aggregateMetrics: this.videoAnalytics.aggregateMetrics,
            topicEngagement: this.videoAnalytics.topicVideoEngagement,
            sessions: this.videoAnalytics.sessions.map((session) => ({
                videoUrl: session.videoUrl,
                participantId: session.participantId,
                topicId: session.topicId,
                metrics: session.metrics,
                eventSummary: {
                    totalEvents: session.events?.length || 0,
                    playCount: session.metrics?.playCount || 0,
                    pauseCount: session.metrics?.pauseCount || 0,
                    seekCount: session.metrics?.seekCount || 0,
                },
                watchTime: session.metrics?.totalWatchTime || 0,
                completionRate: session.metrics?.completionPercentage || 0,
            })),
        };
    }

    private canAttemptConnection(): boolean {
        const now = Date.now();
        const windowStart =
            now - this.connectionAttemptWindowMinutes * 60 * 1000;

        // Remove old attempts outside the time window
        this.connectionAttemptTimestamps =
            this.connectionAttemptTimestamps.filter(
                (timestamp) => timestamp > windowStart,
            );

        // Check if we're under the limit
        return (
            this.connectionAttemptTimestamps.length < this.maxConnectionAttempts
        );
    }

    private trackConnectionAttempt(): void {
        this.connectionAttemptTimestamps.push(Date.now());
    }

    /**
     * Shown when we exhaust WebSocket connection attempts and cannot reach an
     * AI agent. Unlike redirectToWelcomePage(), this does NOT reload through the
     * welcome page (which would re-trigger the duplicate-participant lockout).
     *  - Clears the duplicate-participant localStorage flag so the participant is
     *    not wrongly shown "You have already taken this interview" on return.
     *  - Navigates to a dedicated agent-unavailable screen.
     */
    showAgentUnavailable(): void {
        // Terminal path — release the AI playback context + synthetic mic that
        // would otherwise keep rendering silence for the rest of the page life,
        // and stop the resume listeners so a foregrounding doesn't re-run
        // verifyConnectionAfterResume on this dead screen.
        this.closeAiPlaybackContext();
        this.removeResumeListeners();

        const roomId = this.roomService.roomId;

        try {
            localStorage.removeItem(roomId);
        } catch (e) {
            console.warn('localStorage unavailable', e);
        }

        if (roomId && roomId !== '') {
            this.router.navigate([roomId, 'agent-unavailable']);
        } else {
            console.warn(
                'No roomId found in RoomService, redirecting to root',
            );
            this.router.navigate(['/']);
        }
    }

    redirectToWelcomePage(): void {
        console.log(
            'Redirecting to welcome page due to connection limit exceeded',
        );

        try {
            // Get roomId from RoomService (most reliable source)
            const roomId = this.roomService.roomId;

            if (roomId && roomId !== '') {
                // Use page reload with original query string for complete fresh state
                const targetUrl = `/${roomId}${this.originalQueryString}`;
                console.log('Reloading page to:', targetUrl);
                this.performRedirect(targetUrl);
            } else {
                console.warn(
                    'No roomId found in RoomService, redirecting to root',
                );
                this.performRedirect('/');
            }
        } catch (error) {
            console.error('Error during redirect:', error);
            // Fallback to root on any error
            this.performRedirect('/');
        }
    }

    // Separate method to make testing easier by allowing us to spy on redirects
    private performRedirect(url: string): void {
        window.location.href = url;
    }
}
